<app-image-cropper
  #coverImageCropper
  [cropMode]="'banner'"
  [title]="'settings.charity_details_form.cropper_title' | translateLang"
  [hintText]="'settings.charity_details_form.cropper_hint' | translateLang"
  [uploadingText]="'settings.charity_details_form.cropper_uploading' | translateLang"
  (imageCroppedBlob)="onCoverImageCroppedBlob($event)">
</app-image-cropper>

<div class="charity-form">
  <!-- Mobile: Header with title, stepper, and close button -->
  <div class="charity-form__mobile-header">
    <div class="charity-form__mobile-header-content">
      <h2 class="charity-form__mobile-title">{{ getPageTitle() | translateLang }}</h2>

      <!-- Horizontal Stepper -->
      <div class="charity-form__mobile-stepper">
        @for (stepItem of steps; track stepItem.step; let i = $index; let last = $last) {
          <div class="charity-form__mobile-step-wrapper">
            <div class="charity-form__mobile-step-icon"
                 [class.charity-form__mobile-step-icon--active]="isStepActive(stepItem.step)"
                 [class.charity-form__mobile-step-icon--completed]="isStepCompleted(stepItem.step) || hasStepData(stepItem.step)"
                 (click)="goToStep(stepItem.step)"
                 [attr.aria-label]="('settings.charity_details_form.go_to' | translate) + ' ' + stepItem.label">
              <span class="charity-form__mobile-step-number">{{ stepItem.number }}</span>
            </div>
            @if (!last) {
              <div class="charity-form__mobile-step-line"
                   [class.charity-form__mobile-step-line--completed]="isStepCompleted(stepItem.step) || hasStepData(stepItem.step)"
                   [class.charity-form__mobile-step-line--next]="!(isStepCompleted(stepItem.step) || hasStepData(stepItem.step)) && steps[i + 1] && isStepActive(steps[i + 1].step)"></div>
            }
          </div>
        }
      </div>
    </div>

    <!-- Mobile Close Button -->
    <button class="charity-form__close charity-form__close--mobile" (click)="onCancel()" [attr.aria-label]="'settings.charity_details_form.close_aria_label' | translate">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
  </div>

  <div class="charity-form__container">
    <!-- Left Sidebar - Stepper -->
    <div class="charity-form__sidebar">
      <div class="charity-form__stepper">
        @for (stepItem of steps; track stepItem.step) {
          <div class="charity-form__step"
               [class.charity-form__step--active]="isStepActive(stepItem.step)"
               [class.charity-form__step--completed]="isStepCompleted(stepItem.step)"
               [class.charity-form__step--has-data]="hasStepData(stepItem.step)"
               (click)="goToStep(stepItem.step)">
            <div class="charity-form__step-icon">
              <span class="charity-form__step-number">{{ stepItem.number }}</span>
            </div>
            <span class="charity-form__step-label"
                  [class.charity-form__step-label--completed]="isStepCompleted(stepItem.step)"
                  [class.charity-form__step-label--has-data]="hasStepData(stepItem.step)">
              {{ stepItem.label }}
            </span>
          </div>
        }
      </div>

      <!-- Action Buttons in Sidebar -->
      <div class="charity-form__sidebar-actions">
        @if (isDraftStatus) {
          <button
            class="charity-form__button charity-form__button--draft"
            type="button"
            (click)="onSaveAsDraft()">
            {{ 'settings.charity_details_form.save_as_draft' | translate }}
          </button>
        }
        <button
          class="charity-form__button charity-form__button--submit"
          type="button"
          [disabled]="isSubmitDisabled"
          (click)="onSubmit()">
          {{ 'settings.charity_details_form.submit' | translate }}
        </button>
      </div>
    </div>

    <!-- Right Content Area -->
    <div class="charity-form__content">
      <!-- Loading State -->
      @if (isLoadingDropdowns) {
        <div class="charity-form__loading">
          <div class="charity-form__spinner"></div>
          <p>{{ 'settings.charity_details_form.loading_form' | translate }}</p>
        </div>
      }

      <!-- Page Header Bar (reusable for all tabs) -->
      @if (!isLoadingDropdowns && shouldShowPageHeader()) {
      <div class="charity-form__page-header">
        <div class="charity-form__page-header-content">
          <h2 class="charity-form__page-title">{{ getPageTitle() | translateLang }}</h2>
        </div>
        <div class="charity-form__page-header-close">
          <button class="charity-form__close charity-form__close--desktop" (click)="onCancel()" [attr.aria-label]="'Close'" type="button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
      }

      <!-- Details Form -->
      @if (!isLoadingDropdowns && currentStep === FormStep.DETAILS) {
        <div class="charity-form__form-container">
          <form [formGroup]="detailsForm" class="charity-form__form">
            <!-- Basic Information Section -->
            <div class="charity-form__section" formGroupName="basicInformation">
              <div class="charity-form__section-header">
                <h3 class="charity-form__section-title">
                  {{ (isEditMode ? 'settings.charity_details_form.edit_charity_title' : 'settings.charity_details_form.add_charity_title') | translate }}
                </h3>
                <p class="charity-form__section-description">
                  {{ 'settings.charity_details_form.edit_charity_description' | translate }}
                </p>
              </div>

              <!-- Charity Profile with Private Checkbox -->
              @if (!isCharityAdmin) {
                <div class="charity-form__field">
                  <label class="charity-form__label" for="charityProfile">{{ 'settings.charity_details_form.charity_profile_label' | translate }}</label>
                  <div class="charity-form__field-with-checkbox">
                    <select
                      id="charityProfile"
                      class="charity-form__select"
                      formControlName="charityProfile"
                      (change)="onCharityProfileChange($event)">
                      <option [ngValue]="null">{{ 'settings.charity_details_form.select_profile' | translate }}</option>
                      @for (profile of profiles; track profile.uuid) {
                        <option [ngValue]="profile.uuid">
                          {{ profile.name }}
                        </option>
                      }
                    </select>
                    @if (isFieldVisible('private')) {
                      <div class="charity-form__checkbox-group">
                        <input
                          type="checkbox"
                          id="private"
                          class="charity-form__checkbox"
                          formControlName="private">
                        <label class="charity-form__checkbox-label" for="private">{{ 'settings.charity_details_form.private_label' | translate }}</label>
                      </div>
                    }
                  </div>
                </div>
              }

              <!-- Charity Name -->
               @if (isFieldVisible('organizationName')) {
                <div class="charity-form__field">
                  <label class="charity-form__label" for="organizationName">
                    {{ 'settings.charity_details_form.charity_name_label' | translate }}@if (isFieldRequired('organizationName')) {<span class="charity-form__required">*</span>}
                  </label>
                  <input
                    type="text"
                    id="organizationName"
                    class="charity-form__input"
                    formControlName="organizationName"
                    [class.charity-form__input--error]="(detailsForm.get('basicInformation.organizationName')?.invalid && detailsForm.get('basicInformation.organizationName')?.touched) || !!charityNameError"
                    [placeholder]="'settings.charity_details_form.charity_name_placeholder' | translate"
                    maxlength="200">
                  @if (charityNameError) {
                    <div class="charity-form__error">
                      {{ charityNameError }}
                    </div>
                  }
                  @if (detailsForm.get('basicInformation.organizationName')?.hasError('required') && detailsForm.get('basicInformation.organizationName')?.touched && !charityNameError) {
                    <div class="charity-form__error">
                      {{ 'settings.charity_details_form.charity_name_required_error' | translate }}
                    </div>
                  }
                </div>
               }

              <!-- Giving Link - Full Width -->
              @if (isFieldVisible('givingLink')) {
                <div class="charity-form__field-row charity-form__field-row--full" [formGroup]="charityDetailsGroup">
                  <div class="charity-form__field">
                    <label class="charity-form__label" for="givingLink">
                      {{ 'settings.charity_details_form.giving_link_label' | translate }}@if (isFieldRequired('givingLink')) {<span class="charity-form__required">*</span>}
                    </label>
                    <!-- Editable only by Foundation Admin / Super Admin: the control is
                         disabled in applyGivingLinkPermission() (which renders the `disabled`
                         attribute) and readonly here; buildGivingLinkPayloadValue() re-asserts
                         the server value on submit. -->
                    <input
                      type="url"
                      id="givingLink"
                      class="charity-form__input"
                      formControlName="givingLink"
                      [readonly]="!canEditGivingLink"
                      [class.charity-form__input--error]="detailsForm.get('charityDetails.givingLink')?.invalid && detailsForm.get('charityDetails.givingLink')?.touched"
                      [placeholder]="'settings.charity_details_form.giving_link_placeholder' | translate">
                    @if (detailsForm.get('charityDetails.givingLink')?.hasError('required') && detailsForm.get('charityDetails.givingLink')?.touched) {
                      <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                    }
                  </div>
                </div>
              }
            </div>

            <!-- Address Section -->
            @if (isSectionVisible('address')) {
            <div class="charity-form__section" formGroupName="address">
              <div class="charity-form__section-header">
                <h3 class="charity-form__section-title">{{ 'settings.charity_details_form.address_title' | translate }}</h3>
                <p class="charity-form__section-description">{{ 'settings.charity_details_form.address_description' | translate }}</p>
              </div>

              @if (isFieldVisible('streetName') || isFieldVisible('city')) {
                <div class="charity-form__field-row">
                  @if (isFieldVisible('streetName')) {
                    <div class="charity-form__field">
                      <label class="charity-form__label" for="streetName">
                        {{ 'settings.charity_details_form.street_address_label' | translate }}@if (isFieldRequired('streetName')) {<span class="charity-form__required">*</span>}
                      </label>
                      <input
                        type="text"
                        id="streetName"
                        class="charity-form__input"
                        formControlName="streetName"
                        [class.charity-form__input--error]="detailsForm.get('address.streetName')?.invalid && detailsForm.get('address.streetName')?.touched"
                        [placeholder]="'settings.charity_details_form.street_address_placeholder' | translate">
                      @if (detailsForm.get('address.streetName')?.hasError('required') && detailsForm.get('address.streetName')?.touched) {
                        <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                      }
                    </div>
                  }

                  @if (isFieldVisible('city')) {
                    <div class="charity-form__field">
                      <label class="charity-form__label" for="city">
                        {{ 'settings.charity_details_form.city_label' | translate }}@if (isFieldRequired('city')) {<span class="charity-form__required">*</span>}
                      </label>
                      <input
                        type="text"
                        id="city"
                        class="charity-form__input"
                        formControlName="city"
                        [class.charity-form__input--error]="detailsForm.get('address.city')?.invalid && detailsForm.get('address.city')?.touched"
                        [placeholder]="'settings.charity_details_form.city_placeholder' | translate">
                      @if (detailsForm.get('address.city')?.hasError('required') && detailsForm.get('address.city')?.touched) {
                        <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                      }
                    </div>
                  }
                </div>
              }

              @if (isFieldVisible('country') || isFieldVisible('stateProvince')) {
                <div class="charity-form__field-row">
                  @if (isFieldVisible('country')) {
                    <div class="charity-form__field">
                      <label class="charity-form__label" for="country">
                        {{ 'settings.charity_details_form.country_label' | translate }}@if (isFieldRequired('country')) {<span class="charity-form__required">*</span>}
                      </label>
                      <select
                        id="country"
                        class="charity-form__select"
                        formControlName="country"
                        [class.charity-form__input--error]="detailsForm.get('address.country')?.invalid && detailsForm.get('address.country')?.touched">
                        <option [ngValue]="null">{{ 'settings.charity_details_form.country_placeholder' | translate }}</option>
                        @for (country of dropdowns?.countries; track country.uuid) {
                          <option [ngValue]="country.uuid">
                            {{ country.name }}
                          </option>
                        }
                      </select>
                      @if (detailsForm.get('address.country')?.hasError('required') && detailsForm.get('address.country')?.touched) {
                        <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                      }
                    </div>
                  }

                  @if (isFieldVisible('stateProvince')) {
                    <div class="charity-form__field">
                      <label class="charity-form__label" for="stateProvince">
                        {{ 'settings.charity_details_form.state_label' | translate }}@if (isFieldRequired('stateProvince')) {<span class="charity-form__required">*</span>}
                      </label>
                      <input
                        type="text"
                        id="stateProvince"
                        class="charity-form__input"
                        formControlName="stateProvince"
                        [class.charity-form__input--error]="detailsForm.get('address.stateProvince')?.invalid && detailsForm.get('address.stateProvince')?.touched"
                        [placeholder]="'settings.charity_details_form.state_placeholder' | translate">
                      @if (detailsForm.get('address.stateProvince')?.hasError('required') && detailsForm.get('address.stateProvince')?.touched) {
                        <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                      }
                    </div>
                  }
                </div>
              }

              @if (isFieldVisible('landmark')) {
                <div class="charity-form__field">
                  <label class="charity-form__label" for="landmark">
                    {{ 'settings.charity_details_form.landmark_label' | translate }}@if (isFieldRequired('landmark')) {<span class="charity-form__required">*</span>}
                  </label>
                  <input
                    type="text"
                    id="landmark"
                    class="charity-form__input"
                    formControlName="landmark"
                    [class.charity-form__input--error]="detailsForm.get('address.landmark')?.invalid && detailsForm.get('address.landmark')?.touched"
                    [placeholder]="'settings.charity_details_form.landmark_placeholder' | translate">
                  @if (detailsForm.get('address.landmark')?.hasError('required') && detailsForm.get('address.landmark')?.touched) {
                    <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                  }
                </div>
              }
            </div>
            }

            <!-- Social Media Links Section -->
            @if (isSectionVisible('socialMediaLinks')) {
            <div class="charity-form__section" formGroupName="socialMediaLinks">
              <div class="charity-form__section-header">
                <h3 class="charity-form__section-title">{{ 'settings.charity_details_form.social_media_title' | translate }}</h3>
                <p class="charity-form__section-description">{{ 'settings.charity_details_form.social_media_description' | translate }}</p>
              </div>

              <div class="charity-form__social-media-fields">
                <div class="charity-form__field-row">
                  @if (isFieldVisible('linkedin')) {
                    <div class="charity-form__field">
                      <label class="charity-form__label" for="linkedin">
                        {{ 'settings.charity_details_form.linkedin_label' | translate }}@if (isFieldRequired('linkedin')) {<span class="charity-form__required">*</span>}
                      </label>
                      <input
                        type="url"
                        id="linkedin"
                        class="charity-form__input"
                        formControlName="linkedin"
                        [class.charity-form__input--error]="detailsForm.get('socialMediaLinks.linkedin')?.invalid && detailsForm.get('socialMediaLinks.linkedin')?.touched"
                        [placeholder]="'settings.charity_details_form.linkedin_placeholder' | translate">
                      @if (detailsForm.get('socialMediaLinks.linkedin')?.hasError('required') && detailsForm.get('socialMediaLinks.linkedin')?.touched) {
                        <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                      }
                    </div>
                  }

                  @if (isFieldVisible('twitter')) {
                    <div class="charity-form__field">
                      <label class="charity-form__label" for="twitter">
                        {{ 'settings.charity_details_form.x_label' | translate }}@if (isFieldRequired('twitter')) {<span class="charity-form__required">*</span>}
                      </label>
                      <input
                        type="url"
                        id="twitter"
                        class="charity-form__input"
                        formControlName="twitter"
                        [class.charity-form__input--error]="detailsForm.get('socialMediaLinks.twitter')?.invalid && detailsForm.get('socialMediaLinks.twitter')?.touched"
                        [placeholder]="'settings.charity_details_form.x_placeholder' | translate">
                      @if (detailsForm.get('socialMediaLinks.twitter')?.hasError('required') && detailsForm.get('socialMediaLinks.twitter')?.touched) {
                        <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                      }
                    </div>
                  }
                </div>

                <div class="charity-form__field-row">
                  @if (isFieldVisible('facebook')) {
                    <div class="charity-form__field">
                      <label class="charity-form__label" for="facebook">
                        {{ 'settings.charity_details_form.facebook_label' | translate }}@if (isFieldRequired('facebook')) {<span class="charity-form__required">*</span>}
                      </label>
                      <input
                        type="url"
                        id="facebook"
                        class="charity-form__input"
                        formControlName="facebook"
                        [class.charity-form__input--error]="detailsForm.get('socialMediaLinks.facebook')?.invalid && detailsForm.get('socialMediaLinks.facebook')?.touched"
                        [placeholder]="'settings.charity_details_form.facebook_placeholder' | translate">
                      @if (detailsForm.get('socialMediaLinks.facebook')?.hasError('required') && detailsForm.get('socialMediaLinks.facebook')?.touched) {
                        <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                      }
                    </div>
                  }

                  @if (isFieldVisible('instagram')) {
                    <div class="charity-form__field">
                      <label class="charity-form__label" for="instagram">
                        {{ 'settings.charity_details_form.instagram_label' | translate }}@if (isFieldRequired('instagram')) {<span class="charity-form__required">*</span>}
                      </label>
                      <input
                        type="url"
                        id="instagram"
                        class="charity-form__input"
                        formControlName="instagram"
                        [class.charity-form__input--error]="detailsForm.get('socialMediaLinks.instagram')?.invalid && detailsForm.get('socialMediaLinks.instagram')?.touched"
                        [placeholder]="'settings.charity_details_form.instagram_placeholder' | translate">
                      @if (detailsForm.get('socialMediaLinks.instagram')?.hasError('required') && detailsForm.get('socialMediaLinks.instagram')?.touched) {
                        <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                      }
                    </div>
                  }
                </div>
              </div>
            </div>
            }

            <!-- Charity Details Section -->
            @if (isSectionVisible('charityDetails')) {
            <div class="charity-form__additional-fields" formGroupName="charityDetails">
              <!-- Website Field -->
              @if (isFieldVisible('website')) {
                <div class="charity-form__field">
                  <label class="charity-form__label" for="website">
                    {{ 'settings.charity_details_form.charity_website_label' | translate }}@if (isFieldRequired('website')) {<span class="charity-form__required">*</span>}
                  </label>
                  <input
                    type="url"
                    id="website"
                    class="charity-form__input"
                    formControlName="website"
                    [class.charity-form__input--error]="detailsForm.get('charityDetails.website')?.invalid && detailsForm.get('charityDetails.website')?.touched"
                    [placeholder]="'settings.charity_details_form.website_placeholder' | translate">
                  @if (detailsForm.get('charityDetails.website')?.hasError('required') && detailsForm.get('charityDetails.website')?.touched) {
                    <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                  }
                </div>
              }
              <!-- Charity Cause - Full Width -->
              @if (isFieldVisible('cause')) {
                <div class="charity-form__field-row charity-form__field-row--full">
                  <div class="charity-form__field">
                    <label class="charity-form__label" for="cause">
                      {{ 'settings.charity_details_form.charity_cause_label' | translate }}@if (isFieldRequired('cause')) {<span class="charity-form__required">*</span>}
                    </label>
                    <div class="charity-form__multiselect charity-form__multiselect--enhanced" [class.charity-form__multiselect--open]="isCausesDropdownOpen">
                      @if (isCausesDropdownOpen) {
                        <div class="charity-form__multiselect-backdrop"
                            (click)="closeCausesDropdown()"></div>
                      }
                      <div class="charity-form__multiselect-input" (click)="toggleCausesDropdown()"
                          [class.charity-form__multiselect-input--error]="detailsForm.get('charityDetails.cause')?.hasError('required') && detailsForm.get('charityDetails.cause')?.touched">
                        @if (getSelectedCauseName()) {
                          <span class="charity-form__multiselect-selected-text">{{ getSelectedCauseName() }}</span>
                        }
                        @if (!getSelectedCauseName()) {
                          <span class="charity-form__multiselect-placeholder">
                            {{ 'settings.charity_details_form.select_cause' | translate }}
                          </span>
                        }
                        <svg class="charity-form__multiselect-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 10l5 5 5-5z" fill="#71717A"/>
                        </svg>
                      </div>
                      @if (isCausesDropdownOpen) {
                        <div class="charity-form__multiselect-dropdown">
                          <div class="charity-form__multiselect-search">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#71717A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M14 14L11.1 11.1" stroke="#71717A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <input
                              type="text"
                              [(ngModel)]="causeSearchText"
                              [ngModelOptions]="{standalone: true}"
                              [placeholder]="'settings.charity_details_form.search_causes_placeholder' | translate"
                              class="charity-form__multiselect-search-input"
                              (click)="$event.stopPropagation()">
                          </div>
                          <div class="charity-form__multiselect-options">
                            @for (cause of getFilteredCauses(); track cause.uuid) {
                              <div class="charity-form__multiselect-option"
                                  [class.charity-form__multiselect-option--selected]="isCauseSelected(cause.uuid)"
                                  (click)="toggleCauseSelection(cause.uuid)">
                                <span class="charity-form__multiselect-label">{{ cause.name }}</span>
                              </div>
                            }
                            @if (getFilteredCauses().length === 0) {
                              <div class="charity-form__multiselect-empty">
                                {{ 'settings.charity_details_form.no_causes_found' | translate }}
                              </div>
                            }
                          </div>
                        </div>
                      }
                    </div>
                    @if (detailsForm.get('charityDetails.cause')?.hasError('required') && detailsForm.get('charityDetails.cause')?.touched) {
                      <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                    }
                  </div>
                </div>
              }

              <!-- Charity Category - Full Width -->
              @if (isFieldVisible('category')) {
                <div class="charity-form__field-row charity-form__field-row--full">
                <div class="charity-form__field">
                  <label class="charity-form__label" for="category">
                    {{ 'settings.charity_details_form.charity_category_label' | translate }}@if (isFieldRequired('category')) {<span class="charity-form__required">*</span>}
                    <span class="charity-form__count-badge">{{ getCategoryCountText() }}</span>
                  </label>
                  <div class="charity-form__multiselect charity-form__multiselect--enhanced" [class.charity-form__multiselect--open]="isCategoriesDropdownOpen">
                    <!-- Backdrop to close dropdown when clicking outside -->
                    @if (isCategoriesDropdownOpen) {
                      <div class="charity-form__multiselect-backdrop"
                          (click)="closeCategoriesDropdown()"></div>
                    }

                    <div class="charity-form__multiselect-input" (click)="toggleCategoriesDropdown()"
                        [class.charity-form__multiselect-input--error]="detailsForm.get('charityDetails.category')?.hasError('required') && detailsForm.get('charityDetails.category')?.touched">
                      <!-- Selected chips -->
                      @if (selectedCategories.length > 0) {
                        <div class="charity-form__chips-container" #categoryChipsContainerEl>
                          @for (category of getVisibleCategoryChips(); track category.uuid) {
                            <div class="charity-form__chip">
                              <span class="charity-form__chip-text">{{ category.name }}</span>
                              <button class="charity-form__chip-remove" (click)="removeCategoryChip(category.uuid, $event)" type="button">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                              </button>
                            </div>
                          }
                          <!-- +n more badge -->
                          @if (hasMoreCategoryChips()) {
                            <div class="charity-form__chip charity-form__chip--more">
                              <span class="charity-form__chip-text">+{{ getRemainingCategoryChipsCount() }} more</span>
                            </div>
                          }
                        </div>
                      }

                      <!-- Placeholder when no selection -->
                      @if (selectedCategories.length === 0) {
                        <span class="charity-form__multiselect-placeholder">
                          {{ 'settings.charity_details_form.select_categories_placeholder' | translate }}
                        </span>
                      }

                      <!-- Clear all button -->
                      @if (selectedCategories.length > 0) {
                        <button class="charity-form__multiselect-clear" (click)="clearAllCategories($event)" type="button">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                        </button>
                      }

                      <svg class="charity-form__multiselect-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 10l5 5 5-5z" fill="#71717A"/>
                      </svg>
                    </div>

                    @if (isCategoriesDropdownOpen) {
                      <div class="charity-form__multiselect-dropdown">
                      <!-- Search input -->
                      <div class="charity-form__multiselect-search">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#71717A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                          <path d="M14 14L11.1 11.1" stroke="#71717A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <input
                          type="text"
                          [(ngModel)]="categorySearchText"
                          [ngModelOptions]="{standalone: true}"
                          [placeholder]="'settings.charity_details_form.search_categories_placeholder' | translate"
                          class="charity-form__multiselect-search-input"
                          (click)="$event.stopPropagation()">
                      </div>

                      <!-- Options list -->
                      <div class="charity-form__multiselect-options">
                        @for (category of getFilteredCategories(); track category.uuid) {
                          <div class="charity-form__multiselect-option"
                              [class.charity-form__multiselect-option--selected]="isCategorySelected(category.uuid)"
                              [class.charity-form__multiselect-option--disabled]="isCategorySelectionDisabled(category.uuid)"
                              (click)="!isCategorySelectionDisabled(category.uuid) && toggleCategorySelection(category.uuid)">
                            <input
                              type="checkbox"
                              [checked]="isCategorySelected(category.uuid)"
                              [disabled]="isCategorySelectionDisabled(category.uuid)"
                              class="charity-form__multiselect-checkbox"
                              (click)="$event.preventDefault()">
                            <span class="charity-form__multiselect-label">{{ category.name }}</span>
                          </div>
                        }
                        @if (getFilteredCategories().length === 0) {
                          <div class="charity-form__multiselect-empty">
                            {{ 'settings.charity_details_form.no_categories_found' | translate }}
                          </div>
                        }
                      </div>

                      <!-- Footer with limit message -->
                      @if (selectedCategories.length >= maxCategoriesLimit) {
                        <div class="charity-form__multiselect-footer">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 1.33334L8 10.6667M8 14V14.0133M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.31811 11.6819 1.33334 8 1.33334C4.3181 1.33334 1.33334 4.31811 1.33334 8C1.33334 11.6819 4.3181 14.6667 8 14.6667Z" stroke="#B45309" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                          <span class="charity-form__multiselect-limit-message">{{ 'settings.charity_details_form.max_categories_reached' | translate: { max: maxCategoriesLimit } }}</span>
                        </div>
                      }
                    </div>
                    }
                  </div>
                  @if (detailsForm.get('charityDetails.category')?.hasError('required') && detailsForm.get('charityDetails.category')?.touched) {
                    <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                  }
                </div>
                </div>
              }

              <!-- Influenced Countries - Full Width -->
              @if (isFieldVisible('influencedCountries')) {
                <div class="charity-form__field-row charity-form__field-row--full">
                  <div class="charity-form__field">
                    <label class="charity-form__label" for="influencedCountries">
                      {{ 'settings.charity_details_form.influenced_countries_label' | translate }}@if (isFieldRequired('influencedCountries')) {<span class="charity-form__required">*</span>}
                      @if (selectedCountries.length > 0) {
                        <span class="charity-form__count-badge">{{ 'settings.charity_details_form.countries_selected' | translate: { count: selectedCountries.length } }}</span>
                      }
                    </label>
                    <div class="charity-form__multiselect charity-form__multiselect--enhanced" [class.charity-form__multiselect--open]="isCountriesDropdownOpen">
                      <!-- Backdrop to close dropdown when clicking outside -->
                      @if (isCountriesDropdownOpen) {
                        <div class="charity-form__multiselect-backdrop"
                            (click)="closeCountriesDropdown()"></div>
                      }

                      <div class="charity-form__multiselect-input" (click)="toggleCountriesDropdown()"
                          [class.charity-form__multiselect-input--error]="detailsForm.get('charityDetails.influencedCountries')?.hasError('required') && detailsForm.get('charityDetails.influencedCountries')?.touched">
                        <!-- Selected chips -->
                        @if (selectedCountries.length > 0) {
                          <!-- When all countries are selected, show a single "Global" chip -->
                          @if (isGlobalInfluenceSelected) {
                            <div class="charity-form__chips-container">
                              <div class="charity-form__chip">
                                <span class="charity-form__chip-text">{{ 'settings.charity_details_form.global_influence_label' | translate }}</span>
                                <button class="charity-form__chip-remove" (click)="removeGlobalInfluenceChip($event)" type="button">
                                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                  </svg>
                                </button>
                              </div>
                            </div>
                          } @else {
                            <div class="charity-form__chips-container" #countryChipsContainerEl>
                              @for (country of getVisibleCountryChips(); track country.uuid) {
                                <div class="charity-form__chip">
                                  <span class="charity-form__chip-text">{{ country.name }}</span>
                                  <button class="charity-form__chip-remove" (click)="removeCountryChip(country.uuid, $event)" type="button">
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                  </button>
                                </div>
                              }
                              <!-- +n more badge -->
                              @if (hasMoreCountryChips()) {
                                <div class="charity-form__chip charity-form__chip--more">
                                  <span class="charity-form__chip-text">+{{ getRemainingCountryChipsCount() }} more</span>
                                </div>
                              }
                            </div>
                          }
                        }

                        <!-- Placeholder when no selection -->
                        @if (selectedCountries.length === 0) {
                          <span class="charity-form__multiselect-placeholder">
                            {{ 'settings.charity_details_form.select_countries_placeholder' | translate }}
                          </span>
                        }

                        <!-- Clear all button -->
                        @if (selectedCountries.length > 0) {
                          <button class="charity-form__multiselect-clear" (click)="clearAllCountries($event)" type="button">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                          </button>
                        }

                        <svg class="charity-form__multiselect-arrow" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 10l5 5 5-5z" fill="#71717A"/>
                        </svg>
                      </div>

                      @if (isCountriesDropdownOpen) {
                        <div class="charity-form__multiselect-dropdown">
                          <!-- Search input -->
                          <div class="charity-form__multiselect-search">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="#71717A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M14 14L11.1 11.1" stroke="#71717A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <input
                              type="text"
                              [(ngModel)]="countrySearchText"
                              [ngModelOptions]="{standalone: true}"
                              [placeholder]="'settings.charity_details_form.search_countries_placeholder' | translate"
                              class="charity-form__multiselect-search-input"
                              (click)="$event.stopPropagation()">
                          </div>

                          <!-- Options list -->
                          <div class="charity-form__multiselect-options">
                            <!-- Global option: selects/deselects all countries. Hidden while searching. -->
                            @if (!countrySearchText.trim() && dropdowns?.countries?.length) {
                              <div class="charity-form__multiselect-option"
                                  [class.charity-form__multiselect-option--selected]="isGlobalInfluenceSelected"
                                  (click)="toggleGlobalInfluence()">
                                <input
                                  type="checkbox"
                                  [checked]="isGlobalInfluenceSelected"
                                  class="charity-form__multiselect-checkbox"
                                  (click)="$event.preventDefault()">
                                <span class="charity-form__multiselect-label">{{ 'settings.charity_details_form.global_influence_label' | translate }}</span>
                              </div>
                            }
                            @for (country of getFilteredCountries(); track country.uuid) {
                              <div class="charity-form__multiselect-option"
                                  [class.charity-form__multiselect-option--selected]="isCountrySelected(country.uuid)"
                                  (click)="toggleCountrySelection(country.uuid)">
                                <input
                                  type="checkbox"
                                  [checked]="isCountrySelected(country.uuid)"
                                  class="charity-form__multiselect-checkbox"
                                  (click)="$event.preventDefault()">
                                <span class="charity-form__multiselect-label">{{ country.name }}</span>
                              </div>
                            }
                            @if (getFilteredCountries().length === 0) {
                              <div class="charity-form__multiselect-empty">
                                {{ 'settings.charity_details_form.no_countries_found' | translate }}
                              </div>
                            }
                          </div>
                        </div>
                      }
                    </div>
                    @if (detailsForm.get('charityDetails.influencedCountries')?.hasError('required') && detailsForm.get('charityDetails.influencedCountries')?.touched) {
                      <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                    }
                  </div>
                </div>
              }

              <!-- Region (read-only, derived from Influenced Countries) - Full Width -->
              <div class="charity-form__field-row charity-form__field-row--full">
                <div class="charity-form__field">
                  <div class="charity-form__region-header">
                    <label class="charity-form__label" for="regions">
                      {{ 'settings.charity_details_form.region_label' | translate }}
                    </label>
                    <span class="charity-form__region-hint">
                      {{ 'settings.charity_details_form.region_reorder_hint' | translate }}
                    </span>
                  </div>
                  <div class="charity-form__region-field"
                      cdkDropList
                      cdkDropListOrientation="mixed"
                      (cdkDropListDropped)="onRegionDrop($event)">
                    @if (isGlobalInfluenceSelected) {
                      <div class="charity-form__region-chip">
                        <span class="charity-form__region-chip-text">{{ 'settings.charity_details_form.global_influence_label' | translate }}</span>
                      </div>
                    } @else {
                      @for (region of getSelectedRegionChips(); track region.uuid; let i = $index) {
                        <div class="charity-form__region-chip" cdkDrag>
                          <svg class="charity-form__region-chip-handle" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="3" cy="2" r="1" fill="currentColor"/>
                            <circle cx="7" cy="2" r="1" fill="currentColor"/>
                            <circle cx="3" cy="5" r="1" fill="currentColor"/>
                            <circle cx="7" cy="5" r="1" fill="currentColor"/>
                            <circle cx="3" cy="8" r="1" fill="currentColor"/>
                            <circle cx="7" cy="8" r="1" fill="currentColor"/>
                          </svg>
                          <span class="charity-form__region-chip-text">{{ i + 1 }} - {{ region.name }}</span>
                        </div>
                      }
                    }
                  </div>
                </div>
              </div>

              <!-- Target Population and Year Founded - Two Columns -->
              <div class="charity-form__field-row">
                @if (isFieldVisible('targetPopulation')) {
                  <div class="charity-form__field">
                    <label class="charity-form__label" for="targetPopulation">
                      {{ 'settings.charity_details_form.target_population_label' | translate }}@if (isFieldRequired('targetPopulation')) {<span class="charity-form__required">*</span>}
                    </label>
                    <input
                      type="number"
                      id="targetPopulation"
                      class="charity-form__input"
                      formControlName="targetPopulation"
                      [class.charity-form__input--error]="detailsForm.get('charityDetails.targetPopulation')?.invalid && detailsForm.get('charityDetails.targetPopulation')?.touched"
                      [placeholder]="'settings.charity_details_form.target_population_placeholder' | translate"
                      (keydown)="onNumberOnlyKeydown($event)"
                      (paste)="onNumberOnlyPaste($event)">
                    @if (detailsForm.get('charityDetails.targetPopulation')?.hasError('required') && detailsForm.get('charityDetails.targetPopulation')?.touched) {
                      <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                    }
                  </div>
                }

                @if (isFieldVisible('yearFounded')) {
                  <div class="charity-form__field">
                    <label class="charity-form__label" for="yearFounded">
                      {{ 'settings.charity_details_form.year_founded_label' | translate }}@if (isFieldRequired('yearFounded')) {<span class="charity-form__required">*</span>}
                    </label>
                    <input
                      type="number"
                      id="yearFounded"
                      class="charity-form__input"
                      formControlName="yearFounded"
                      [class.charity-form__input--error]="detailsForm.get('charityDetails.yearFounded')?.invalid && detailsForm.get('charityDetails.yearFounded')?.touched"
                      [placeholder]="'settings.charity_details_form.year_founded_placeholder' | translate"
                      min="1800"
                      [max]="currentYear"
                      (keydown)="onNumberOnlyKeydown($event)"
                      (paste)="onNumberOnlyPaste($event)"
                      (input)="onYearInput($event)">
                    @if (detailsForm.get('charityDetails.yearFounded')?.hasError('required') && detailsForm.get('charityDetails.yearFounded')?.touched) {
                      <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                    }
                  </div>
                }
              </div>


              <!-- Description Textareas -->
              @if (isFieldVisible('executiveSummary')) {
                <div class="charity-form__field">
                  <div class="charity-form__label-row">
                    <label class="charity-form__label" for="executiveSummary">
                      {{ 'settings.charity_details_form.executive_summary_label' | translate }}@if (isFieldRequired('executiveSummary')) {<span class="charity-form__required">*</span>}
                    </label>
                    <span class="charity-form__char-count">{{ getCharacterCount('executiveSummary') }}</span>
                  </div>
                  <textarea
                    id="executiveSummary"
                    class="charity-form__textarea"
                    formControlName="executiveSummary"
                    rows="3"
                    maxlength="1000"
                    [class.charity-form__textarea--error]="detailsForm.get('charityDetails.executiveSummary')?.invalid && detailsForm.get('charityDetails.executiveSummary')?.touched"
                    [placeholder]="'settings.charity_details_form.executive_summary_placeholder' | translate"></textarea>
                  @if (detailsForm.get('charityDetails.executiveSummary')?.hasError('required') && detailsForm.get('charityDetails.executiveSummary')?.touched) {
                    <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                  }
                </div>
              }

              @if (isFieldVisible('visionStatement')) {
                <div class="charity-form__field">
                  <div class="charity-form__label-row">
                    <label class="charity-form__label" for="visionStatement">
                      {{ 'settings.charity_details_form.vision_statement_label' | translate }}@if (isFieldRequired('visionStatement')) {<span class="charity-form__required">*</span>}
                    </label>
                    <span class="charity-form__char-count">{{ getCharacterCount('visionStatement') }}</span>
                  </div>
                  <textarea
                    id="visionStatement"
                    class="charity-form__textarea"
                    formControlName="visionStatement"
                    rows="3"
                    maxlength="1000"
                    [class.charity-form__textarea--error]="detailsForm.get('charityDetails.visionStatement')?.invalid && detailsForm.get('charityDetails.visionStatement')?.touched"
                    [placeholder]="'settings.charity_details_form.vision_statement_placeholder' | translate"></textarea>
                  @if (detailsForm.get('charityDetails.visionStatement')?.hasError('required') && detailsForm.get('charityDetails.visionStatement')?.touched) {
                    <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                  }
                </div>
              }

              @if (isFieldVisible('missionStatement')) {
                <div class="charity-form__field">
                  <div class="charity-form__label-row">
                    <label class="charity-form__label" for="missionStatement">
                      {{ 'settings.charity_details_form.mission_statement_label' | translate }}@if (isFieldRequired('missionStatement')) {<span class="charity-form__required">*</span>}
                    </label>
                    <span class="charity-form__char-count">{{ getCharacterCount('missionStatement') }}</span>
                  </div>
                  <textarea
                    id="missionStatement"
                    class="charity-form__textarea"
                    formControlName="missionStatement"
                    rows="3"
                    maxlength="1000"
                    [class.charity-form__textarea--error]="detailsForm.get('charityDetails.missionStatement')?.invalid && detailsForm.get('charityDetails.missionStatement')?.touched"
                    [placeholder]="'settings.charity_details_form.mission_statement_placeholder' | translate"></textarea>
                  @if (detailsForm.get('charityDetails.missionStatement')?.hasError('required') && detailsForm.get('charityDetails.missionStatement')?.touched) {
                    <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                  }
                </div>
              }

              @if (isFieldVisible('history')) {
                <div class="charity-form__field">
                  <div class="charity-form__label-row">
                    <label class="charity-form__label" for="history">
                      {{ 'settings.charity_details_form.history_label' | translate }}@if (isFieldRequired('history')) {<span class="charity-form__required">*</span>}
                    </label>
                    <span class="charity-form__char-count">{{ getCharacterCount('history') }}</span>
                  </div>
                  <textarea
                    id="history"
                    class="charity-form__textarea"
                    formControlName="history"
                    rows="3"
                    maxlength="1000"
                    [class.charity-form__textarea--error]="detailsForm.get('charityDetails.history')?.invalid && detailsForm.get('charityDetails.history')?.touched"
                    [placeholder]="'settings.charity_details_form.history_placeholder' | translate"></textarea>
                  @if (detailsForm.get('charityDetails.history')?.hasError('required') && detailsForm.get('charityDetails.history')?.touched) {
                    <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                  }
                </div>
              }
            </div>
            }

            @if (isFieldVisible('coverPhoto')) {
            <div class="charity-form__section charity-form__section--upload">
              <div class="charity-form__upload-container">
                <div class="charity-form__upload-description">
                  <h4 class="charity-form__upload-title">{{ 'settings.charity_details_form.cover_photo_title' | translate }}@if (isFieldRequired('coverPhoto')) {<span class="charity-form__required">*</span>}</h4>
                  <p class="charity-form__upload-text">
                    {{ 'settings.charity_details_form.cover_photo_description' | translate }}
                  </p>
                </div>
                <div class="charity-form__upload-area"
                    (click)="coverImageFileInput.click()"
                    [class.charity-form__upload-area--uploading]="isUploadingCoverImage">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4V16M12 4L8 8M12 4L16 8M4 17V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H18C18.5304 21 19.0391 20.7893 19.4142 20.4142C19.7893 20.0391 20 19.5304 20 19V17" stroke="#606060" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <p class="charity-form__upload-label">
                    {{ isUploadingCoverImage ? ('settings.charity_details_form.uploading' | translate) : ('settings.charity_details_form.upload_images' | translate) }}
                  </p>
                  <p class="charity-form__upload-formats">{{ 'settings.charity_details_form.image_formats' | translate }}</p>
                </div>
              </div>

              <!-- Hidden file input for cover image -->
              <input
                type="file"
                #coverImageFileInput
                id="coverImageFileInput"
                accept=".jpg,.jpeg,.bmp,.png,.jfif,.webp,image/jpeg,image/jpg,image/bmp,image/png,image/webp"
                style="display: none;"
                (change)="onCoverImageFileSelected($event)">

              <!-- Uploaded cover image preview -->
              @if (uploadedCoverImage) {
                <div class="charity-form__file-preview">
                  <div class="charity-form__file-preview-content">
                    <svg class="charity-form__file-preview-icon" viewBox="0 0 16.5 16.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.6893 6.575C10.1809 5.81833 5.43092 9.90833 5.75009 15.3333M3.66676 4.91667C3.66676 5.35869 3.84235 5.78262 4.15491 6.09518C4.46747 6.40774 4.8914 6.58333 5.33342 6.58333C5.77545 6.58333 6.19938 6.40774 6.51194 6.09518C6.8245 5.78262 7.00009 5.35869 7.00009 4.91667C7.00009 4.47464 6.8245 4.05072 6.51194 3.73816C6.19938 3.42559 5.77545 3.25 5.33342 3.25C4.8914 3.25 4.46747 3.42559 4.15491 3.73816C3.84235 4.05072 3.66676 4.47464 3.66676 4.91667Z" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M0.750091 9.13833C3.06676 8.8175 5.14592 9.93667 6.27009 11.7217" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M0.750091 6.08333C0.750091 4.21667 0.750092 3.28333 1.11342 2.57C1.433 1.94282 1.94291 1.43291 2.57009 1.11333C3.28342 0.75 4.21676 0.75 6.08342 0.75H10.4168C12.2834 0.75 13.2168 0.75 13.9301 1.11333C14.5573 1.43291 15.0672 1.94282 15.3868 2.57C15.7501 3.28333 15.7501 4.21667 15.7501 6.08333V10.4167C15.7501 12.2833 15.7501 13.2167 15.3868 13.93C15.0672 14.5572 14.5573 15.0671 13.9301 15.3867C13.2168 15.75 12.2834 15.75 10.4168 15.75H6.08342C4.21676 15.75 3.28342 15.75 2.57009 15.3867C1.94291 15.0671 1.433 14.5572 1.11342 13.93C0.750092 13.2167 0.750091 12.2833 0.750091 10.4167V6.08333Z" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span
                      class="charity-form__file-preview-name"
                      [customTooltip]="(uploadedCoverImage.fileName | filenameExtractor)"
                      [tooltipDisabled]="(uploadedCoverImage.fileName | filenameExtractor).length <= 80">
                      {{ uploadedCoverImage.fileName | filenameExtractor | truncateFilename }}
                    </span>
                  </div>
                  <div class="charity-form__file-preview-actions">
                    <button
                      type="button"
                      class="charity-form__file-preview-button charity-form__file-preview-button--preview"
                      (click)="previewCoverImage()">
                      {{ 'settings.charity_details_form.preview' | translate }}
                    </button>
                    <button
                      type="button"
                      class="charity-form__file-preview-button charity-form__file-preview-button--delete"
                      (click)="deleteCoverImage()">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              }

            </div>
            }

            <!-- File Upload Sections -->
            @if (isFieldVisible('logo')) {
            <div class="charity-form__section charity-form__section--upload">
              <div class="charity-form__upload-container">
                <div class="charity-form__upload-description">
                  <h4 class="charity-form__upload-title">{{ 'settings.charity_details_form.charity_logo_title' | translate }}@if (isFieldRequired('logo')) {<span class="charity-form__required">*</span>}</h4>
                  <p class="charity-form__upload-text">
                    {{ 'settings.charity_details_form.charity_logo_description' | translate }}
                  </p>
                </div>
                <div class="charity-form__upload-area"
                    (click)="onUploadLogoClick()"
                    [class.charity-form__upload-area--uploading]="isUploadingLogo">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4V16M12 4L8 8M12 4L16 8M4 17V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H18C18.5304 21 19.0391 20.7893 19.4142 20.4142C19.7893 20.0391 20 19.5304 20 19V17" stroke="#606060" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <p class="charity-form__upload-label">
                    {{ isUploadingLogo ? ('settings.charity_details_form.uploading' | translate) : ('settings.charity_details_form.upload_logo' | translate) }}
                  </p>
                  <p class="charity-form__upload-formats">{{ 'settings.charity_details_form.image_formats' | translate }}</p>
                </div>
              </div>

              <!-- Hidden file input for logo -->
              <input
                type="file"
                id="logoFileInput"
                accept=".jpg,.jpeg,.bmp,.png,.jfif,.webp,image/jpeg,image/jpg,image/bmp,image/png,image/webp"
                style="display: none;"
                (change)="onLogoFileSelected($event)">

              <!-- Uploaded logo preview -->
              @if (uploadedLogo) {
                <div class="charity-form__file-preview">
                  <div class="charity-form__file-preview-content">
                    <svg class="charity-form__file-preview-icon" viewBox="0 0 16.5 16.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.6893 6.575C10.1809 5.81833 5.43092 9.90833 5.75009 15.3333M3.66676 4.91667C3.66676 5.35869 3.84235 5.78262 4.15491 6.09518C4.46747 6.40774 4.8914 6.58333 5.33342 6.58333C5.77545 6.58333 6.19938 6.40774 6.51194 6.09518C6.8245 5.78262 7.00009 5.35869 7.00009 4.91667C7.00009 4.47464 6.8245 4.05072 6.51194 3.73816C6.19938 3.42559 5.77545 3.25 5.33342 3.25C4.8914 3.25 4.46747 3.42559 4.15491 3.73816C3.84235 4.05072 3.66676 4.47464 3.66676 4.91667Z" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M0.750091 9.13833C3.06676 8.8175 5.14592 9.93667 6.27009 11.7217" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M0.750091 6.08333C0.750091 4.21667 0.750092 3.28333 1.11342 2.57C1.433 1.94282 1.94291 1.43291 2.57009 1.11333C3.28342 0.75 4.21676 0.75 6.08342 0.75H10.4168C12.2834 0.75 13.2168 0.75 13.9301 1.11333C14.5573 1.43291 15.0672 1.94282 15.3868 2.57C15.7501 3.28333 15.7501 4.21667 15.7501 6.08333V10.4167C15.7501 12.2833 15.7501 13.2167 15.3868 13.93C15.0672 14.5572 14.5573 15.0671 13.9301 15.3867C13.2168 15.75 12.2834 15.75 10.4168 15.75H6.08342C4.21676 15.75 3.28342 15.75 2.57009 15.3867C1.94291 15.0671 1.433 14.5572 1.11342 13.93C0.750092 13.2167 0.750091 12.2833 0.750091 10.4167V6.08333Z" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span
                      class="charity-form__file-preview-name"
                      [customTooltip]="(uploadedLogo.fileName | filenameExtractor)"
                      [tooltipDisabled]="(uploadedLogo.fileName | filenameExtractor).length <= 80">
                      {{ uploadedLogo.fileName | filenameExtractor | truncateFilename }}
                    </span>
                  </div>
                  <div class="charity-form__file-preview-actions">
                    <button
                      type="button"
                      class="charity-form__file-preview-button charity-form__file-preview-button--preview"
                      (click)="previewLogo()">
                      {{ 'settings.charity_details_form.preview' | translate }}
                    </button>
                    <button
                      type="button"
                      class="charity-form__file-preview-button charity-form__file-preview-button--delete"
                      (click)="deleteLogo()">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4m2 0v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667a1.333 1.333 0 0 1-1.334-1.334V4h9.334Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              }
            </div>
            }

            <!-- Mobile Back/Next navigation (visible only on mobile) -->
            <div class="charity-form__mobile-nav">
              <button
                type="button"
                class="charity-form__mobile-nav-btn"
                [disabled]="isBackDisabled()"
                (click)="onBack()">
                {{ 'settings.charity_details_form.back' | translate }}
              </button>
              <button
                type="button"
                class="charity-form__mobile-nav-btn"
                (click)="onNext()">
                {{ 'settings.charity_details_form.next' | translate }}
              </button>
            </div>

            <!-- Navigation Buttons -->
            <div class="charity-form__navigation">
              <button
                class="charity-form__nav-button charity-form__nav-button--back"
                type="button"
                [disabled]="isBackDisabled()"
                (click)="onBack()">
                {{ 'settings.charity_details_form.back' | translate }}
              </button>
              <button
                class="charity-form__nav-button charity-form__nav-button--next"
                type="button"
                (click)="onNext()">
                {{ 'settings.charity_details_form.next' | translate }}
              </button>
            </div>
          </form>
        </div>
      }

      <!-- Team Form -->
      @if (!isLoadingDropdowns && currentStep === FormStep.TEAM) {
        <div class="charity-form__form-container">
          <form [formGroup]="teamForm" class="charity-form__form">

            <!-- Charity Leader Section -->
            @if (isSectionVisible('charityLeader')) {
            <div class="charity-form__section">
                <div class="charity-form__section-header">
                  <h3 class="charity-form__section-title">{{ 'settings.charity_details_form.charity_leader_title' | translate }}</h3>
                  <p class="charity-form__section-description">
                    {{ 'settings.charity_details_form.add_team_members_description' | translate }}
                  </p>
                </div>

              <div class="charity-form__leader-container">
                <!-- Profile Photo Upload -->
                <div class="charity-form__photo-upload">
                  <div class="charity-form__photo-upload-area"
                      (click)="onUploadLeaderPictureClick()"
                      [class.charity-form__upload-area--uploading]="isUploadingLeaderPicture">
                    @if (uploadedLeaderPicture) {
                      <img [src]="uploadedLeaderPicture.fileUrl"
                          class="charity-form__add-photo-preview"
                          alt="Leader photo">
                    } @else {
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2V26M14 2L8 8M14 2L20 8M4 22V24C4 24.5304 4.21071 25.0391 4.58579 25.4142C4.96086 25.7893 5.46957 26 6 26H22C22.5304 26 23.0391 25.7893 23.4142 25.4142C23.7893 25.0391 24 24.5304 24 24V22" stroke="#606060" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      <p class="charity-form__photo-upload-text">
                        {{ isUploadingLeaderPicture ? ('settings.charity_details_form.uploading' | translate) : ('settings.charity_details_form.upload_profile_photo' | translate) }}
                      </p>
                    }
                  </div>
                </div>

                <!-- Hidden file input for leader picture -->
                <input
                  type="file"
                  id="leaderPictureFileInput"
                  accept=".jpg,.jpeg,.bmp,.png,.jfif,.webp,image/jpeg,image/jpg,image/bmp,image/png,image/webp"
                  style="display: none;"
                  (change)="onLeaderPictureFileSelected($event)">

                <!-- Leader Fields -->
                <div class="charity-form__leader-fields">
                  @if (isFieldVisible('leaderName')) {
                    <div class="charity-form__field-row">
                      <div class="charity-form__field" style="grid-column: 1 / -1">
                        <label class="charity-form__label" for="leaderName">
                          {{ 'settings.charity_details_form.full_name_label' | translate }}@if (isFieldRequired('leaderName')) {<span class="charity-form__required">*</span>}
                        </label>
                        <input
                          type="text"
                          id="leaderName"
                          class="charity-form__input"
                          formControlName="leaderName"
                          [class.charity-form__input--error]="teamForm.get('leaderName')?.invalid && teamForm.get('leaderName')?.touched"
                          [placeholder]="'settings.charity_details_form.full_name_placeholder' | translate">
                        @if (teamForm.get('leaderName')?.hasError('required') && teamForm.get('leaderName')?.touched) {
                          <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                        }
                      </div>
                    </div>
                  }

                  @if (isFieldVisible('leaderEmail') || isFieldVisible('leaderLinkedin')) {
                    <div class="charity-form__field-row">
                      @if (isFieldVisible('leaderEmail')) {
                        <div class="charity-form__field">
                          <label class="charity-form__label" for="leaderEmail">
                            {{ 'settings.charity_details_form.email_address_label' | translate }}@if (isFieldRequired('leaderEmail')) {<span class="charity-form__required">*</span>}
                          </label>
                          <input
                            type="email"
                            id="leaderEmail"
                            class="charity-form__input"
                            formControlName="leaderEmail"
                            [class.charity-form__input--error]="teamForm.get('leaderEmail')?.invalid && teamForm.get('leaderEmail')?.touched"
                            [placeholder]="'settings.charity_details_form.email_placeholder' | translate">
                          @if (teamForm.get('leaderEmail')?.hasError('required') && teamForm.get('leaderEmail')?.touched) {
                            <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                          }
                        </div>
                      }

                      @if (isFieldVisible('leaderLinkedin')) {
                        <div class="charity-form__field">
                          <label class="charity-form__label" for="leaderLinkedin">
                            {{ 'settings.charity_details_form.linkedin_profile_label' | translate }}@if (isFieldRequired('leaderLinkedin')) {<span class="charity-form__required">*</span>}
                          </label>
                          <input
                            type="url"
                            id="leaderLinkedin"
                            class="charity-form__input"
                            formControlName="leaderLinkedin"
                            [class.charity-form__input--error]="teamForm.get('leaderLinkedin')?.invalid && teamForm.get('leaderLinkedin')?.touched"
                            [placeholder]="'settings.charity_details_form.linkedin_url_placeholder' | translate">
                          @if (teamForm.get('leaderLinkedin')?.hasError('required') && teamForm.get('leaderLinkedin')?.touched) {
                            <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                          }
                        </div>
                      }
                    </div>
                  }

                </div>

                @if (isFieldVisible('leaderBio')) {
                  <div class="charity-form__field charity-form__field--full-width">
                    <div class="charity-form__label-row">
                      <label class="charity-form__label" for="leaderBio">
                        {{ 'settings.charity_details_form.bio_label' | translate }}@if (isFieldRequired('leaderBio')) {<span class="charity-form__required">*</span>}
                      </label>
                      <span class="charity-form__char-count">{{ 'settings.charity_details_form.char_limit_500' | translate }}</span>
                    </div>
                    <input
                      type="text"
                      id="leaderBio"
                      class="charity-form__input"
                      formControlName="leaderBio"
                      [class.charity-form__input--error]="teamForm.get('leaderBio')?.invalid && teamForm.get('leaderBio')?.touched"
                      maxlength="500"
                      [placeholder]="'settings.charity_details_form.bio_placeholder' | translate">
                    @if (teamForm.get('leaderBio')?.hasError('required') && teamForm.get('leaderBio')?.touched) {
                      <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                    }
                  </div>
                }
              </div>

              <!-- Uploaded leader picture preview -->
              @if (uploadedLeaderPicture) {
              <div class="charity-form__file-preview">
                <div class="charity-form__file-preview-content">
                  <svg class="charity-form__file-preview-icon" viewBox="0 0 16.5 16.5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.6893 6.575C10.1809 5.81833 5.43092 9.90833 5.75009 15.3333M3.66676 4.91667C3.66676 5.35869 3.84235 5.78262 4.15491 6.09518C4.46747 6.40774 4.8914 6.58333 5.33342 6.58333C5.77545 6.58333 6.19938 6.40774 6.51194 6.09518C6.8245 5.78262 7.00009 5.35869 7.00009 4.91667C7.00009 4.47464 6.8245 4.05072 6.51194 3.73816C6.19938 3.42559 5.77545 3.25 5.33342 3.25C4.8914 3.25 4.46747 3.42559 4.15491 3.73816C3.84235 4.05072 3.66676 4.47464 3.66676 4.91667Z" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M0.750091 9.13833C3.06676 8.8175 5.14592 9.93667 6.27009 11.7217" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M0.750091 6.08333C0.750091 4.21667 0.750092 3.28333 1.11342 2.57C1.433 1.94282 1.94291 1.43291 2.57009 1.11333C3.28342 0.75 4.21676 0.75 6.08342 0.75H10.4168C12.2834 0.75 13.2168 0.75 13.9301 1.11333C14.5573 1.43291 15.0672 1.94282 15.3868 2.57C15.7501 3.28333 15.7501 4.21667 15.7501 6.08333V10.4167C15.7501 12.2833 15.7501 13.2167 15.3868 13.93C15.0672 14.5572 14.5573 15.0671 13.9301 15.3867C13.2168 15.75 12.2834 15.75 10.4168 15.75H6.08342C4.21676 15.75 3.28342 15.75 2.57009 15.3867C1.94291 15.0671 1.433 14.5572 1.11342 13.93C0.750092 13.2167 0.750091 12.2833 0.750091 10.4167V6.08333Z" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span
                    class="charity-form__file-preview-name"
                    [customTooltip]="(uploadedLeaderPicture.fileName | filenameExtractor)"
                    [tooltipDisabled]="(uploadedLeaderPicture.fileName | filenameExtractor).length <= 80">
                    {{ uploadedLeaderPicture.fileName | filenameExtractor | truncateFilename }}
                  </span>
                </div>
                <div class="charity-form__file-preview-actions">
                  <button
                    type="button"
                    class="charity-form__file-preview-button charity-form__file-preview-button--preview"
                    (click)="previewLeaderPicture()">
                    {{ 'settings.charity_details_form.preview' | translate }}
                  </button>
                  <button
                    type="button"
                    class="charity-form__file-preview-button charity-form__file-preview-button--delete"
                    (click)="deleteLeaderPicture()">
                    <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 3.5h10M4.25 3.5V2a.75.75 0 0 1 .75-.75h2a.75.75 0 0 1 .75.75v1.5M2.25 3.5v7.75A1.75 1.75 0 0 0 4 13h4a1.75 1.75 0 0 0 1.75-1.75V3.5H2.25ZM4.75 6v4.5M7.25 6v4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
              }
            </div>
            }

            <!-- Number of Team Members
            @if (isFieldVisible('numberOfTeamMembers')) {
              <div class="charity-form__field charity-form__field--full-width">
                <label class="charity-form__label" for="numberOfTeamMembers">
                  {{ 'settings.charity_details_form.number_of_team_members_label' | translate }}@if (isFieldRequired('numberOfTeamMembers')) {<span class="charity-form__required">*</span>}
                </label>
                <input
                  type="number"
                  id="numberOfTeamMembers"
                  class="charity-form__input"
                  formControlName="numberOfTeamMembers"
                  [placeholder]="'settings.charity_details_form.number_placeholder' | translate"
                  min="0">
              </div>
            } -->

            <!-- Executive Team Section -->
            @if (isSectionVisible('executiveTeam')) {
            <div class="charity-form__section">
                <div class="charity-form__section-header">
                  <h3 class="charity-form__section-title">{{ 'settings.charity_details_form.executive_team_title' | translate }}</h3>
                  <p class="charity-form__section-description">
                    {{ 'settings.charity_details_form.add_team_members_executive_description' | translate }}
                  </p>
                </div>

              <!-- Existing Executive Team Members -->
              @for (member of executiveTeamMembers; track $index; let i = $index) {
                <app-team-member-card
                  [memberForm]="getExistingExecutiveMemberForm(i)"
                  [photoUrl]="member.profilePicture?.url || ''"
                  nameTitleControlName="fullName"
                  emailControlName="email"
                  bioControlName="bio"
                  linkedinControlName="linkedinProfile"
                  [nameTitleRequired]="isFieldRequired('executiveName')"
                  [emailRequired]="isFieldRequired('executiveEmail')"
                  [bioRequired]="isFieldRequired('executiveBio')"
                  [linkedinRequired]="isFieldRequired('executiveLinkedin')"
                  [nameTitleVisible]="isFieldVisible('executiveName')"
                  [emailVisible]="isFieldVisible('executiveEmail')"
                  [bioVisible]="isFieldVisible('executiveBio')"
                  [linkedinVisible]="isFieldVisible('executiveLinkedin')"
                  [showRemoveButton]="true"
                  (photoFileSelected)="onExistingExecutivePhotoFile(i, $event)"
                  (remove)="deleteExecutiveTeamMember(i)">
                </app-team-member-card>
              }

              <!-- Add New Executive Team Member Form -->
              <app-team-member-card
                [memberForm]="teamForm"
                [photoUrl]="uploadedExecutivePicture?.fileUrl || ''"
                [cardTitle]="'settings.charity_details_form.add_team_member_title' | translate"
                nameTitleControlName="executiveName"
                emailControlName="executiveEmail"
                bioControlName="executiveBio"
                linkedinControlName="executiveLinkedin"
                [nameTitleRequired]="isFieldRequired('executiveName')"
                [emailRequired]="isFieldRequired('executiveEmail')"
                [bioRequired]="isFieldRequired('executiveBio')"
                [linkedinRequired]="isFieldRequired('executiveLinkedin')"
                [nameTitleVisible]="isFieldVisible('executiveName')"
                [emailVisible]="isFieldVisible('executiveEmail')"
                [bioVisible]="isFieldVisible('executiveBio')"
                [linkedinVisible]="isFieldVisible('executiveLinkedin')"
                [showCommitButton]="true"
                [commitLabel]="editingExecutiveIndex !== null ? ('settings.charity_details_form.update_button' | translate) : ('settings.charity_details_form.add_button' | translate)"
                [removeLabel]="'settings.charity_details_form.remove_photo' | translate"
                (photoFileSelected)="onExecutivePhotoFile($event)"
                (commit)="addExecutiveTeamMember()"
                (remove)="deleteExecutivePicture()">
              </app-team-member-card>
            </div>
            }

            <!-- Board Member Section -->
            @if (isSectionVisible('boardMembers')) {
            <div class="charity-form__section">
                <div class="charity-form__section-header">
                  <h3 class="charity-form__section-title">{{ 'settings.charity_details_form.board_member_title' | translate }}</h3>
                  <p class="charity-form__section-description">
                    {{ 'settings.charity_details_form.add_charity_description' | translate }}
                  </p>
                </div>

              <!-- Existing Board Members -->
              @for (member of boardMembers; track $index; let i = $index) {
                <app-team-member-card
                  [memberForm]="getExistingBoardMemberForm(i)"
                  [photoUrl]="member.profilePicture?.url || ''"
                  nameTitleControlName="fullName"
                  emailControlName="email"
                  bioControlName="bio"
                  linkedinControlName="linkedinProfile"
                  [nameTitleRequired]="isFieldRequired('boardMemberName')"
                  [emailRequired]="isFieldRequired('boardMemberEmail')"
                  [bioRequired]="isFieldRequired('boardMemberBio')"
                  [linkedinRequired]="isFieldRequired('boardMemberLinkedin')"
                  [nameTitleVisible]="isFieldVisible('boardMemberName')"
                  [emailVisible]="isFieldVisible('boardMemberEmail')"
                  [bioVisible]="isFieldVisible('boardMemberBio')"
                  [linkedinVisible]="isFieldVisible('boardMemberLinkedin')"
                  [showRemoveButton]="true"
                  (photoFileSelected)="onExistingBoardMemberPhotoFile(i, $event)"
                  (remove)="deleteBoardMember(i)">
                </app-team-member-card>
              }

              <!-- Add New Board Member Form -->
              <app-team-member-card
                [memberForm]="teamForm"
                [photoUrl]="uploadedBoardMemberPicture?.fileUrl || ''"
                [cardTitle]="'settings.charity_details_form.add_board_member_title' | translate"
                nameTitleControlName="boardMemberName"
                emailControlName="boardMemberEmail"
                bioControlName="boardMemberBio"
                linkedinControlName="boardMemberLinkedin"
                [nameTitleRequired]="isFieldRequired('boardMemberName')"
                [emailRequired]="isFieldRequired('boardMemberEmail')"
                [bioRequired]="isFieldRequired('boardMemberBio')"
                [linkedinRequired]="isFieldRequired('boardMemberLinkedin')"
                [nameTitleVisible]="isFieldVisible('boardMemberName')"
                [emailVisible]="isFieldVisible('boardMemberEmail')"
                [bioVisible]="isFieldVisible('boardMemberBio')"
                [linkedinVisible]="isFieldVisible('boardMemberLinkedin')"
                [showCommitButton]="true"
                [commitLabel]="editingBoardMemberIndex !== null ? ('settings.charity_details_form.update_button' | translate) : ('settings.charity_details_form.add_button' | translate)"
                [removeLabel]="'settings.charity_details_form.remove_photo' | translate"
                (photoFileSelected)="onBoardMemberPhotoFile($event)"
                (commit)="addBoardMember()"
                (remove)="deleteBoardMemberPicture()">
              </app-team-member-card>
            </div>
            }

            <!-- Partner Organizations Section -->
            @if (isSectionVisible('partnerOrganizations')) {
            <div class="charity-form__section">
                <div class="charity-form__section-header">
                  <h3 class="charity-form__section-title">{{ 'settings.charity_details_form.partner_organizations_title' | translate }}</h3>
                  <p class="charity-form__section-description">
                    {{ 'settings.charity_details_form.partner_organizations_description' | translate }}
                  </p>
                </div>

              <!-- Existing Partner Organizations -->
              @for (partner of partnerOrganizations; track $index; let i = $index) {
                <div class="charity-form__add-section">
                  <div class="charity-form__field-row">
                    @if (isFieldVisible('partnerName')) {
                      <div class="charity-form__field">
                        <label class="charity-form__label">
                          {{ 'settings.charity_details_form.charity_name_label' | translate }}
                        </label>
                        <input
                          type="text"
                          class="charity-form__input"
                          [value]="partner.name"
                          (input)="updatePartnerField(i, 'name', $any($event.target).value)"
                          [placeholder]="'settings.charity_details_form.charity_name_placeholder' | translate">
                      </div>
                    }
                    @if (isFieldVisible('partnerEmail')) {
                      <div class="charity-form__field">
                        <label class="charity-form__label">
                          {{ 'settings.charity_details_form.email_address_label' | translate }}
                        </label>
                        <input
                          type="email"
                          class="charity-form__input"
                          [value]="partner.email"
                          (input)="updatePartnerField(i, 'email', $any($event.target).value)"
                          [placeholder]="'settings.charity_details_form.email_placeholder' | translate">
                      </div>
                    }
                  </div>
                  @if (isFieldVisible('partnerDescription')) {
                    <div class="charity-form__field">
                      <div class="charity-form__label-row">
                        <label class="charity-form__label">
                          {{ 'settings.charity_details_form.relationship_description_label' | translate }}
                        </label>
                        <span class="charity-form__char-count">{{ 'settings.charity_details_form.char_limit_500' | translate }}</span>
                      </div>
                      <input
                        type="text"
                        class="charity-form__input"
                        [value]="partner.relationshipDescription"
                        (input)="updatePartnerField(i, 'relationshipDescription', $any($event.target).value)"
                        maxlength="500"
                        [placeholder]="'settings.charity_details_form.relationship_description_placeholder' | translate">
                    </div>
                  }
                  <div class="charity-form__add-button-wrapper">
                    <button
                      type="button"
                      class="charity-form__action-button charity-form__action-button--delete"
                      (click)="deletePartnerOrganization(i)">
                      <svg width="12" height="14" viewBox="0 0 13.3333 14.6666" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0.666667 3.33329H12.6667" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M11.334 3.33329V12.6666C11.334 13.3333 10.6673 14 10.0007 14H3.33398C2.66732 14 2.00065 13.3333 2.00065 12.6666V3.33329" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M4.00065 3.33333V2C4.00065 1.33333 4.66732 0.666667 5.33398 0.666667H8.00065C8.66732 0.666667 9.33398 1.33333 9.33398 2V3.33333" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M5.33268 6.66667V10.6667" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M8.00065 6.66667V10.6667" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              }

              <!-- Add New Partner Organization -->
              <div class="charity-form__add-section">
                <h4 class="charity-form__add-section-title">{{ 'settings.charity_details_form.add_partner_organization_title' | translate }}</h4>

                <div class="charity-form__field-row">
                  @if (isFieldVisible('partnerName')) {
                    <div class="charity-form__field">
                      <label class="charity-form__label" for="partnerName">
                        {{ 'settings.charity_details_form.charity_name_label' | translate }}@if (isFieldRequired('partnerName')) {<span class="charity-form__required">*</span>}
                      </label>
                      <input
                        type="text"
                        id="partnerName"
                        class="charity-form__input"
                        [class.charity-form__input--error]="teamForm.get('partnerName')?.invalid && teamForm.get('partnerName')?.touched"
                        formControlName="partnerName"
                        [placeholder]="'settings.charity_details_form.charity_name_placeholder' | translate">
                      @if (teamForm.get('partnerName')?.hasError('required') && teamForm.get('partnerName')?.touched) {
                        <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                      }
                    </div>
                  }
                  @if (isFieldVisible('partnerEmail')) {
                    <div class="charity-form__field">
                      <label class="charity-form__label" for="partnerEmail">
                        {{ 'settings.charity_details_form.email_address_label' | translate }}@if (isFieldRequired('partnerEmail')) {<span class="charity-form__required">*</span>}
                      </label>
                      <input
                        type="email"
                        id="partnerEmail"
                        class="charity-form__input"
                        [class.charity-form__input--error]="teamForm.get('partnerEmail')?.invalid && teamForm.get('partnerEmail')?.touched"
                        formControlName="partnerEmail"
                        [placeholder]="'settings.charity_details_form.email_placeholder' | translate">
                      @if (teamForm.get('partnerEmail')?.hasError('required') && teamForm.get('partnerEmail')?.touched) {
                        <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                      }
                    </div>
                  }
                </div>

                @if (isFieldVisible('partnerDescription')) {
                  <div class="charity-form__field">
                    <div class="charity-form__label-row">
                      <label class="charity-form__label" for="partnerDescription">
                        {{ 'settings.charity_details_form.relationship_description_label' | translate }}@if (isFieldRequired('partnerDescription')) {<span class="charity-form__required">*</span>}
                      </label>
                      <span class="charity-form__char-count">{{ 'settings.charity_details_form.char_limit_500' | translate }}</span>
                    </div>
                    <input
                      type="text"
                      id="partnerDescription"
                      class="charity-form__input"
                      [class.charity-form__input--error]="teamForm.get('partnerDescription')?.invalid && teamForm.get('partnerDescription')?.touched"
                      formControlName="partnerDescription"
                      maxlength="500"
                      [placeholder]="'settings.charity_details_form.relationship_description_placeholder' | translate">
                    @if (teamForm.get('partnerDescription')?.hasError('required') && teamForm.get('partnerDescription')?.touched) {
                      <div class="charity-form__error">{{ 'settings.charity_details_form.required_field' | translate }}</div>
                    }
                  </div>
                }

                <div class="charity-form__add-button-wrapper">
                  <button
                    type="button"
                    class="charity-form__add-button"
                    (click)="addPartnerOrganization()">
                    {{ 'settings.charity_details_form.add_button' | translate }}
                  </button>
                </div>
              </div>
            </div>
            }

            <!-- Mobile Back/Next navigation (visible only on mobile) -->
            <div class="charity-form__mobile-nav">
              <button
                type="button"
                class="charity-form__mobile-nav-btn"
                [disabled]="isBackDisabled()"
                (click)="onBack()">
                {{ 'settings.charity_details_form.back' | translate }}
              </button>
              <button
                type="button"
                class="charity-form__mobile-nav-btn"
                (click)="onNext()">
                {{ 'settings.charity_details_form.next' | translate }}
              </button>
            </div>

            <!-- Navigation Buttons -->
            <div class="charity-form__navigation">
              <button
                class="charity-form__nav-button charity-form__nav-button--back"
                type="button"
                [disabled]="isBackDisabled()"
                (click)="onBack()">
                {{ 'settings.charity_details_form.back' | translate }}
              </button>
              <button
                class="charity-form__nav-button charity-form__nav-button--next"
                type="button"
                (click)="onNext()">
                {{ 'settings.charity_details_form.next' | translate }}
              </button>
            </div>
          </form>
        </div>
      }

      <!-- Financials Form -->
      @if (!isLoadingDropdowns && currentStep === FormStep.FINANCIALS) {
      <div class="charity-form__form-container charity-form__form-container--financials">
        <form [formGroup]="financialsForm" class="charity-form__form">

          <!-- Header Section -->
          <div class="charity-form__financials-header">
            <div class="charity-form__financials-header-content">
              <h3 class="charity-form__financials-title">
                {{ 'settings.charity_details_form.financials.title' | translateLang }}@if (isAnyFinancialsFieldRequired()) {<span class="charity-form__required">*</span>}
              </h3>
              <p class="charity-form__financials-description">
                {{ 'settings.charity_details_form.financials.description' | translateLang }}
              </p>
            </div>
            <button
              class="charity-form__button charity-form__button--add-year"
              type="button"
              (click)="addNewBudgetYear()">
              {{ 'settings.charity_details_form.financials.add_new_year' | translateLang }}
            </button>
          </div>

          <!-- Budget Year Containers -->
          @for (budgetYear of budgetYears; track $index; let i = $index) {
          <div class="charity-form__financials-container"
               [class.charity-form__financials-container--active]="i === currentBudgetYearIndex">

            <!-- Remove Year Button -->
            <button
              class="charity-form__close charity-form__close--desktop charity-form__financials-close"
              (click)="removeBudgetYear(i)"
              type="button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <div class="charity-form__financials-content">

              <!-- Year, Fiscal Year, Currency Row -->
              @if (isSectionVisible('financialPeriod')) {
              <div class="charity-form__field-row charity-form__field-row--three-col">
                <!-- Year Dropdown -->
                @if (isFieldVisible('budgetYear')) {
                <div class="charity-form__field">
                  <label class="charity-form__label" [attr.for]="'year-' + i">
                    {{ 'settings.charity_details_form.financials.year' | translateLang }}@if (isFinancialsFieldRequired('budgetYear')) {<span class="charity-form__required">*</span>}
                  </label>
                  <div class="charity-form__dropdown-wrapper">
                    <button
                      type="button"
                      class="charity-form__dropdown-trigger"
                      [class.charity-form__dropdown-trigger--error]="i === currentBudgetYearIndex && financialsYearError"
                      (click)="toggleYearDropdown(i)">
                      {{ budgetYear.year || ('settings.charity_details_form.financials.select_year' | translateLang) }}
                      <svg class="charity-form__dropdown-icon" [class.charity-form__dropdown-icon--open]="isYearDropdownOpen && currentBudgetYearIndex === i" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <app-dropdown
                      [items]="getAvailableYearItems(i)"
                      [isOpen]="isYearDropdownOpen && currentBudgetYearIndex === i"
                      [selectedItem]="getYearItemForBudget(budgetYear)"
                      [maxHeight]="'200px'"
                      (itemSelected)="onYearSelectedForBudget(i, $event)"
                      (closed)="isYearDropdownOpen = false">
                    </app-dropdown>
                  </div>
                  @if (i === currentBudgetYearIndex && financialsYearError) {
                  <span class="charity-form__error">
                    {{ financialsYearError }}
                  </span>
                  }
                </div>
                }

                <!-- Fiscal Year End Datepicker -->
                @if (isFieldVisible('fiscalYearEnd')) {
                <div class="charity-form__field">
                  <label class="charity-form__label" [attr.for]="'fiscalYearEnd-' + i">
                    {{ 'settings.charity_details_form.financials.fiscal_year_end' | translateLang }}@if (isFinancialsFieldRequired('fiscalYearEnd')) {<span class="charity-form__required">*</span>}
                  </label>
                  <mat-form-field class="charity-form__datepicker-field"
                                  [class.charity-form__datepicker-field--error]="i === currentErrorBudgetYearIndex && financialsFiscalYearError"
                                  appearance="outline">
                    <input
                      matInput
                      [matDatepicker]="fiscalYearPicker"
                      [value]="getFiscalYearEndDate(budgetYear)"
                      (dateChange)="onFiscalYearEndChange(i, $event.value)"
                      (click)="fiscalYearPicker.open()"
                      placeholder="MM/DD"
                      readonly>
                    <mat-datepicker-toggle matIconSuffix [for]="fiscalYearPicker"></mat-datepicker-toggle>
                    <mat-datepicker #fiscalYearPicker
                      [calendarHeaderComponent]="customDatepickerHeader"
                      [startAt]="getFiscalYearPickerStartDate(budgetYear)"
                      panelClass="charity-form-datepicker"></mat-datepicker>
                  </mat-form-field>
                  @if (i === currentErrorBudgetYearIndex && financialsFiscalYearError) {
                  <span class="charity-form__error">
                    {{ financialsFiscalYearError }}
                  </span>
                  }
                </div>
                }

                <!-- Currency Dropdown -->
                @if (isFieldVisible('budgetCurrency')) {
                <div class="charity-form__field">
                  <label class="charity-form__label" [attr.for]="'currency-' + i">
                    {{ 'settings.charity_details_form.financials.currency' | translateLang }}@if (isFinancialsFieldRequired('budgetCurrency')) {<span class="charity-form__required">*</span>}
                  </label>
                  <div class="charity-form__dropdown-wrapper">
                    <button
                      type="button"
                      class="charity-form__dropdown-trigger"
                      [class.charity-form__dropdown-trigger--error]="i === currentErrorBudgetYearIndex && financialsCurrencyError"
                      (click)="toggleCurrencyDropdown(i)">
                      {{ budgetYear.currency || ('settings.charity_details_form.financials.select_currency' | translateLang) }}
                      <svg class="charity-form__dropdown-icon" [class.charity-form__dropdown-icon--open]="isCurrencyDropdownOpen && currentBudgetYearIndex === i" width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                    <app-dropdown
                      [items]="currencyDropdownItems"
                      [isOpen]="isCurrencyDropdownOpen && currentBudgetYearIndex === i"
                      [selectedItem]="getCurrencyItemForBudget(budgetYear)"
                      [maxHeight]="'200px'"
                      (itemSelected)="onCurrencySelectedForBudget(i, $event)"
                      (closed)="isCurrencyDropdownOpen = false">
                    </app-dropdown>
                  </div>
                  @if (i === currentErrorBudgetYearIndex && financialsCurrencyError) {
                  <span class="charity-form__error">
                    {{ financialsCurrencyError }}
                  </span>
                  }
                </div>
                }
              </div>
              }

              <!-- Revenue Section -->
              @if (isSectionVisible('financialRevenue')) {
              <div class="charity-form__financials-section">
                <div class="charity-form__financials-section-header">
                  <h4 class="charity-form__financials-section-title">{{ 'settings.charity_details_form.financials.revenue_title' | translateLang }}@if (isFinancialsSectionRequired('revenue')) {<span class="charity-form__required">*</span>}</h4>
                  <p class="charity-form__financials-section-subtitle">{{ 'settings.charity_details_form.financials.revenue_subtitle' | translateLang }}</p>
                </div>

                <!-- Revenue Items Table -->
                <div class="charity-form__financials-table">
                  <!-- Column Headers -->
                  <div class="charity-form__financials-row charity-form__financials-row--header">
                    <div class="charity-form__financials-fields">
                      @if (isFieldVisible('revenueItemName')) {
                      <div class="charity-form__field">
                        <label class="charity-form__label-small">
                          {{ 'settings.charity_details_form.financials.item_name' | translateLang }}@if (isFinancialsFieldRequired('revenueItemName')) {<span class="charity-form__required">*</span>}
                        </label>
                      </div>
                      }
                      @if (isFieldVisible('revenueBudget')) {
                      <div class="charity-form__field">
                        <label class="charity-form__label-small">
                          {{ 'settings.charity_details_form.financials.budget' | translateLang }}@if (isFinancialsFieldRequired('revenueBudget')) {<span class="charity-form__required">*</span>}
                        </label>
                      </div>
                      }
                      @if (isFieldVisible('revenueActuals')) {
                      <div class="charity-form__field">
                        <label class="charity-form__label-small">
                          {{ 'settings.charity_details_form.financials.actual' | translateLang }}@if (isFinancialsFieldRequired('revenueActuals')) {<span class="charity-form__required">*</span>}
                        </label>
                      </div>
                      }
                    </div>
                    <!-- Empty space for delete button column -->
                    <div class="charity-form__financials-header-spacer"></div>
                  </div>

                  <!-- Revenue Items -->
                  @for (item of budgetYear.revenueItems; track $index; let revenueIndex = $index) {
                  <div class="charity-form__financials-row">
                    <div class="charity-form__financials-fields">
                      @if (isFieldVisible('revenueItemName')) {
                      <div class="charity-form__field">
                        <input
                          type="text"
                          [id]="'revenueItemName_' + i + '_' + revenueIndex"
                          class="charity-form__input"
                          [class.charity-form__input--error]="(financialsItemValidationAttempted || isItemStarted(item)) && isFinancialsFieldRequired('revenueItemName') && !item.itemName?.trim()"
                          [(ngModel)]="item.itemName"
                          [ngModelOptions]="{standalone: true}"
                          (ngModelChange)="markFinancialsItemStarted()"
                          (blur)="onFinancialsItemFieldBlur(item, 'revenueItemName', !item.itemName?.trim())"
                          [placeholder]="'settings.charity_details_form.financials.item_name' | translateLang"
                          maxlength="200">
                      </div>
                      }

                      @if (isFieldVisible('revenueBudget')) {
                      <div class="charity-form__field">
                        <input
                          type="number"
                          [id]="'revenueBudget_' + i + '_' + revenueIndex"
                          class="charity-form__input"
                          [class.charity-form__input--error]="(financialsItemValidationAttempted || isItemStarted(item)) && isFinancialsFieldRequired('revenueBudget') && item.budget == null"
                          [(ngModel)]="item.budget"
                          [ngModelOptions]="{standalone: true}"
                          (ngModelChange)="markFinancialsItemStarted()"
                          (blur)="onFinancialsItemFieldBlur(item, 'revenueBudget', item.budget == null)"
                          placeholder="0.00"
                          step="0.01">
                      </div>
                      }

                      @if (isFieldVisible('revenueActuals')) {
                      <div class="charity-form__field">
                        <input
                          type="number"
                          [id]="'revenueActual_' + i + '_' + revenueIndex"
                          class="charity-form__input"
                          [class.charity-form__input--error]="(financialsItemValidationAttempted || isItemStarted(item)) && isFinancialsFieldRequired('revenueActuals') && item.actual == null"
                          [(ngModel)]="item.actual"
                          [ngModelOptions]="{standalone: true}"
                          (ngModelChange)="markFinancialsItemStarted()"
                          (blur)="onFinancialsItemFieldBlur(item, 'revenueActuals', item.actual == null)"
                          placeholder="0.00"
                          step="0.01">
                      </div>
                      }
                    </div>

                    <button
                      type="button"
                      class="charity-form__delete-button"
                      (click)="deleteRevenueItem(i, revenueIndex)"
                      [attr.aria-label]="'settings.charity_details_form.financials.delete_revenue_item' | translateLang">
                      <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 3.5h9M4.5 3.5V2.16667C4.5 1.93665 4.59658 1.71631 4.76849 1.5544C4.94039 1.39248 5.17355 1.3 5.41667 1.3h1.16667c0.23012 0 0.46028 0.09248 0.63218 0.2544 0.17191 0.16191 0.26815 0.38225 0.26815 0.61227V3.5m1.5 0v7C9 10.73 8.90342 10.9503 8.73152 11.1122 8.55961 11.2741 8.32645 11.3667 8.08333 11.3667H3.91667c-0.23012 0-0.46028-0.0926-0.63218-0.2545-0.17191-0.1619-0.26815-0.3822-0.26815-0.6122v-7h7z" stroke="#96080B" stroke-width="1.333" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                  }
                </div>

                <!-- Add Revenue Item Button -->
                <button
                  type="button"
                  class="charity-form__button charity-form__button--add-item"
                  (click)="addRevenueItem(i)">
                  {{ 'settings.charity_details_form.financials.add_revenue_item' | translateLang }}
                </button>

                <!-- Revenue Totals -->
                @if (isSectionVisible('financialTotals')) {
                <div class="charity-form__financials-totals">
                  <div class="charity-form__financials-total-label">
                    <p class="charity-form__financials-total-title">{{ 'settings.charity_details_form.financials.total_revenue' | translateLang }}</p>
                    <p class="charity-form__financials-total-subtitle">{{ 'settings.charity_details_form.financials.total_revenue_subtitle' | translateLang }}</p>
                  </div>
                  @if (isFieldVisible('totalBudgetRevenues')) {
                  <div class="charity-form__financials-total-value">
                    <p class="charity-form__financials-total-label-text">
                      {{ 'settings.charity_details_form.financials.total_budget_revenue' | translateLang }}@if (isFinancialsFieldRequired('totalBudgetRevenues')) {<span class="charity-form__required">*</span>}
                    </p>
                    <div class="charity-form__financials-total-amount">{{ formatCurrency(getTotalRevenueBudget(i)) }}</div>
                  </div>
                  }
                  @if (isFieldVisible('totalActualsRevenues')) {
                  <div class="charity-form__financials-total-value">
                    <p class="charity-form__financials-total-label-text">
                      {{ 'settings.charity_details_form.financials.total_actual_revenue' | translateLang }}@if (isFinancialsFieldRequired('totalActualsRevenues')) {<span class="charity-form__required">*</span>}
                    </p>
                    <div class="charity-form__financials-total-amount">{{ formatCurrency(getTotalRevenueActual(i)) }}</div>
                  </div>
                  }
                </div>
                }
              </div>
              }

              <!-- Expenses Section -->
              @if (isSectionVisible('financialExpenses')) {
              <div class="charity-form__financials-section">
                <div class="charity-form__financials-section-header">
                  <h4 class="charity-form__financials-section-title">{{ 'settings.charity_details_form.financials.expenses_title' | translateLang }}@if (isFinancialsSectionRequired('expenses')) {<span class="charity-form__required">*</span>}</h4>
                  <p class="charity-form__financials-section-subtitle">{{ 'settings.charity_details_form.financials.expenses_subtitle' | translateLang }}</p>
                </div>

                <!-- Expense Items Table -->
                <div class="charity-form__financials-table">
                  <!-- Column Headers -->
                  <div class="charity-form__financials-row charity-form__financials-row--header">
                    <div class="charity-form__financials-fields">
                      @if (isFieldVisible('expenseItemName')) {
                      <div class="charity-form__field">
                        <label class="charity-form__label-small">
                          {{ 'settings.charity_details_form.financials.item_name' | translateLang }}@if (isFinancialsFieldRequired('expenseItemName')) {<span class="charity-form__required">*</span>}
                        </label>
                      </div>
                      }
                      @if (isFieldVisible('expenseBudget')) {
                      <div class="charity-form__field">
                        <label class="charity-form__label-small">
                          {{ 'settings.charity_details_form.financials.budget' | translateLang }}@if (isFinancialsFieldRequired('expenseBudget')) {<span class="charity-form__required">*</span>}
                        </label>
                      </div>
                      }
                      @if (isFieldVisible('expenseActuals')) {
                      <div class="charity-form__field">
                        <label class="charity-form__label-small">
                          {{ 'settings.charity_details_form.financials.actual' | translateLang }}@if (isFinancialsFieldRequired('expenseActuals')) {<span class="charity-form__required">*</span>}
                        </label>
                      </div>
                      }
                    </div>
                    <!-- Empty space for delete button column -->
                    <div class="charity-form__financials-header-spacer"></div>
                  </div>

                  <!-- Expense Items -->
                  @for (item of budgetYear.expenseItems; track $index; let expenseIndex = $index) {
                  <div class="charity-form__financials-row">
                    <div class="charity-form__financials-fields">
                      @if (isFieldVisible('expenseItemName')) {
                      <div class="charity-form__field">
                        <input
                          type="text"
                          [id]="'expenseItemName_' + i + '_' + expenseIndex"
                          class="charity-form__input"
                          [class.charity-form__input--error]="(financialsItemValidationAttempted || isItemStarted(item)) && isFinancialsFieldRequired('expenseItemName') && !item.itemName?.trim()"
                          [(ngModel)]="item.itemName"
                          [ngModelOptions]="{standalone: true}"
                          (ngModelChange)="markFinancialsItemStarted()"
                          (blur)="onFinancialsItemFieldBlur(item, 'expenseItemName', !item.itemName?.trim())"
                          [placeholder]="'settings.charity_details_form.financials.item_name' | translateLang"
                          maxlength="200">
                      </div>
                      }

                      @if (isFieldVisible('expenseBudget')) {
                      <div class="charity-form__field">
                        <input
                          type="number"
                          [id]="'expenseBudget_' + i + '_' + expenseIndex"
                          class="charity-form__input"
                          [class.charity-form__input--error]="(financialsItemValidationAttempted || isItemStarted(item)) && isFinancialsFieldRequired('expenseBudget') && item.budget == null"
                          [(ngModel)]="item.budget"
                          [ngModelOptions]="{standalone: true}"
                          (ngModelChange)="markFinancialsItemStarted()"
                          (blur)="onFinancialsItemFieldBlur(item, 'expenseBudget', item.budget == null)"
                          placeholder="0.00"
                          step="0.01">
                      </div>
                      }

                      @if (isFieldVisible('expenseActuals')) {
                      <div class="charity-form__field">
                        <input
                          type="number"
                          [id]="'expenseActual_' + i + '_' + expenseIndex"
                          class="charity-form__input"
                          [class.charity-form__input--error]="(financialsItemValidationAttempted || isItemStarted(item)) && isFinancialsFieldRequired('expenseActuals') && item.actual == null"
                          [(ngModel)]="item.actual"
                          [ngModelOptions]="{standalone: true}"
                          (ngModelChange)="markFinancialsItemStarted()"
                          (blur)="onFinancialsItemFieldBlur(item, 'expenseActuals', item.actual == null)"
                          placeholder="0.00"
                          step="0.01">
                      </div>
                      }
                    </div>

                    <button
                      type="button"
                      class="charity-form__delete-button"
                      (click)="deleteExpenseItem(i, expenseIndex)"
                      [attr.aria-label]="'settings.charity_details_form.financials.delete_expense_item' | translateLang">
                      <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1.5 3.5h9M4.5 3.5V2.16667C4.5 1.93665 4.59658 1.71631 4.76849 1.5544 4.94039 1.39248 5.17355 1.3 5.41667 1.3h1.16667c0.23012 0 0.46028 0.09248 0.63218 0.2544 0.17191 0.16191 0.26815 0.38225 0.26815 0.61227V3.5m1.5 0v7C9 10.73 8.90342 10.9503 8.73152 11.1122 8.55961 11.2741 8.32645 11.3667 8.08333 11.3667H3.91667c-0.23012 0-0.46028-0.0926-0.63218-0.2545-0.17191-0.1619-0.26815-0.3822-0.26815-0.6122v-7h7z" stroke="#96080B" stroke-width="1.333" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </button>
                  </div>
                  }
                </div>

                <!-- Add Expense Item Button -->
                <button
                  type="button"
                  class="charity-form__button charity-form__button--add-item"
                  (click)="addExpenseItem(i)">
                  {{ 'settings.charity_details_form.financials.add_expense_item' | translateLang }}
                </button>

                <!-- Expense Totals -->
                @if (isSectionVisible('financialTotals')) {
                <div class="charity-form__financials-totals">
                  <div class="charity-form__financials-total-label">
                    <p class="charity-form__financials-total-title">{{ 'settings.charity_details_form.financials.total_expenses' | translateLang }}</p>
                    <p class="charity-form__financials-total-subtitle">{{ 'settings.charity_details_form.financials.total_expenses_subtitle' | translateLang }}</p>
                  </div>
                  @if (isFieldVisible('totalBudgetExpenses')) {
                  <div class="charity-form__financials-total-value">
                    <p class="charity-form__financials-total-label-text">
                      {{ 'settings.charity_details_form.financials.total_operating_budget' | translateLang }}@if (isFinancialsFieldRequired('totalBudgetExpenses')) {<span class="charity-form__required">*</span>}
                    </p>
                    <div class="charity-form__financials-total-amount">{{ formatCurrency(getTotalExpenseBudget(i)) }}</div>
                  </div>
                  }
                  @if (isFieldVisible('totalActualsExpenses')) {
                  <div class="charity-form__financials-total-value">
                    <p class="charity-form__financials-total-label-text">
                      {{ 'settings.charity_details_form.financials.total_actual_budget' | translateLang }}@if (isFinancialsFieldRequired('totalActualsExpenses')) {<span class="charity-form__required">*</span>}
                    </p>
                    <div class="charity-form__financials-total-amount">{{ formatCurrency(getTotalExpenseActual(i)) }}</div>
                  </div>
                  }
                </div>
                }

                <!-- Net Gain/Loss -->
                @if (isFieldVisible('netGainLossBudget') || isFieldVisible('netGainLossActuals')) {
                <div class="charity-form__financials-totals charity-form__financials-totals--net">
                  <div class="charity-form__financials-total-label">
                    <p class="charity-form__financials-total-title">{{ 'settings.charity_details_form.financials.net_gain_loss' | translateLang }}</p>
                    <p class="charity-form__financials-total-subtitle">{{ 'settings.charity_details_form.financials.net_gain_loss_subtitle' | translateLang }}</p>
                  </div>
                  @if (isFieldVisible('netGainLossBudget')) {
                  <div class="charity-form__financials-total-value">
                    <p class="charity-form__financials-total-label-text">
                      {{ 'settings.charity_details_form.financials.net_gain_loss_budget' | translateLang }}@if (isFinancialsFieldRequired('netGainLossBudget')) {<span class="charity-form__required">*</span>}
                    </p>
                    <div class="charity-form__financials-total-amount"
                         [class.charity-form__financials-total-amount--positive]="getNetGainLossBudget(i) > 0"
                         [class.charity-form__financials-total-amount--negative]="getNetGainLossBudget(i) < 0">
                      {{ formatCurrency(getNetGainLossBudget(i)) }}
                    </div>
                  </div>
                  }
                  @if (isFieldVisible('netGainLossActuals')) {
                  <div class="charity-form__financials-total-value">
                    <p class="charity-form__financials-total-label-text">
                      {{ 'settings.charity_details_form.financials.actual_net_gain_loss' | translateLang }}@if (isFinancialsFieldRequired('netGainLossActuals')) {<span class="charity-form__required">*</span>}
                    </p>
                    <div class="charity-form__financials-total-amount"
                         [class.charity-form__financials-total-amount--positive]="getNetGainLossActual(i) > 0"
                         [class.charity-form__financials-total-amount--negative]="getNetGainLossActual(i) < 0">
                      {{ formatCurrency(getNetGainLossActual(i)) }}
                    </div>
                  </div>
                  }
                </div>
                }
              </div>
              }

            </div>
          </div>
          }

          <!-- Mobile Back/Next navigation (visible only on mobile) -->
          <div class="charity-form__mobile-nav">
            <button
              type="button"
              class="charity-form__mobile-nav-btn"
              [disabled]="isBackDisabled()"
              (click)="onBack()">
              {{ 'settings.charity_details_form.back' | translate }}
            </button>
            <button
              type="button"
              class="charity-form__mobile-nav-btn"
              (click)="onNext()">
              {{ 'settings.charity_details_form.next' | translate }}
            </button>
          </div>

          <!-- Navigation Buttons -->
          <div class="charity-form__navigation">
            <button
              class="charity-form__nav-button charity-form__nav-button--back"
              type="button"
              [disabled]="isBackDisabled()"
              (click)="onBack()">
              {{ 'settings.charity_details_form.back' | translate }}
            </button>
            <button
              class="charity-form__nav-button charity-form__nav-button--next"
              type="button"
              (click)="onNext()">
              {{ 'settings.charity_details_form.next' | translate }}
            </button>
          </div>
        </form>
      </div>
      }

      <!-- Documents Step -->
      @if (!isLoadingDropdowns && currentStep === FormStep.DOCUMENTS) {
      <div class="charity-form__form-container charity-form__form-container--documents charity-form__form-container--documents-step">
        <!-- Documents Content -->
        <div class="charity-form__documents-content">
          <!-- Mobile section header (title + description, visible only on mobile) -->
          <div class="charity-form__documents-mobile-header">
            <p class="charity-form__documents-title">
              {{ 'settings.charity_details_form.documents_page_title' | translate }}
            </p>
            <p class="charity-form__documents-mobile-desc">
              {{ 'settings.charity_details_form.documents.description' | translate }}@if (isFieldRequired('organisationDocuments')) {<span class="charity-form__required">*</span>}
            </p>
          </div>

          <!-- Description (desktop only) -->
          <p class="charity-form__documents-description">
            {{ 'settings.charity_details_form.documents.description' | translate }}@if (isFieldRequired('organisationDocuments')) {<span class="charity-form__required">*</span>}
          </p>
          @if (documentsNextAttempted && isFieldRequired('organisationDocuments') && !hasValidDocumentsData()) {
            <p class="charity-form__upload-inline-error">{{ 'settings.charity_details_form.documents.error_required' | translate }}</p>
          }

          <!-- Upload Area -->
          <div class="charity-form__documents-upload-area"
               (dragover)="onDocumentDragOver($event)"
               (drop)="onDocumentDrop($event)"
               (click)="documentFileInput.click()">
            <div class="charity-form__documents-upload-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.3334 5.33333L8.00002 2L4.66669 5.33333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 2V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <p class="charity-form__documents-upload-text">
              {{ 'settings.charity_details_form.documents.upload_files' | translate }}
            </p>
            <p class="charity-form__documents-upload-formats">
              {{ 'settings.charity_details_form.documents.supported_formats' | translate }}
            </p>
            <input
              #documentFileInput
              type="file"
              class="charity-form__file-input"
              accept=".txt,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              (change)="onDocumentFileSelected($event)"
              hidden>
          </div>

          <!-- Uploading Progress Indicator -->
          @if (isUploadingDocument) {
          <div class="charity-form__upload-progress">
            <div class="charity-form__upload-progress-info">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.6667 1.66666H5C4.55797 1.66666 4.13405 1.84225 3.82149 2.15481C3.50893 2.46737 3.33333 2.89129 3.33333 3.33332V16.6667C3.33333 17.1087 3.50893 17.5326 3.82149 17.8452C4.13405 18.1577 4.55797 18.3333 5 18.3333H15C15.442 18.3333 15.866 18.1577 16.1785 17.8452C16.4911 17.5326 16.6667 17.1087 16.6667 16.6667V6.66666L11.6667 1.66666Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.6667 1.66666V6.66666H16.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="charity-form__upload-progress-text">{{ 'settings.charity_details_form.documents.uploading' | translate }}</span>
            </div>
            <div class="charity-form__upload-progress-bar">
              <div class="charity-form__upload-progress-bar-fill"></div>
            </div>
          </div>
          }

          <!-- Uploaded Files Section -->
          @if (uploadedDocuments.length > 0) {
          <div class="charity-form__documents-list-section">
            <div class="charity-form__documents-list-header">
              <p class="charity-form__documents-list-title">
                <span class="charity-form__documents-list-title-text">
                  {{ 'settings.charity_details_form.documents.uploaded_files' | translate }}
                </span>
              </p>
            </div>

            <div class="charity-form__documents-list">
              @for (document of uploadedDocuments; track $index) {
              <div class="charity-form__document-item"
                   [class.charity-form__document-item--error]="document.status === 'error'">
                <div class="charity-form__document-info">
                  @if (document.status === 'error') {
                  <div class="charity-form__document-icon charity-form__document-icon--error">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="1.5"/>
                      <path d="M10 6V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      <circle cx="10" cy="13" r="0.5" fill="currentColor"/>
                    </svg>
                  </div>
                  } @else {
                  <div class="charity-form__document-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                      <path d="M3.9975 4.83167H6.71833M3.9975 8.45917H11.2525M3.9975 12.0867H11.2525M14.5 5.59917V12.7392C14.4835 13.2062 14.3746 13.6653 14.1798 14.0901C13.9851 14.5149 13.7081 14.8969 13.365 15.2142C13.0216 15.5329 12.6188 15.7809 12.1795 15.9439C11.7403 16.1069 11.2732 16.1817 10.805 16.1642H4.48C4.00895 16.1857 3.53828 16.114 3.09498 15.9533C2.65168 15.7926 2.24446 15.5459 1.89667 15.2275C1.55022 14.9094 1.27045 14.5256 1.07361 14.0984C0.876758 13.6712 0.766759 13.2092 0.75 12.7392V4.1775C0.766545 3.71048 0.875352 3.25135 1.07015 2.82657C1.26495 2.40179 1.54188 2.01977 1.885 1.7025C2.2284 1.38377 2.63123 1.13582 3.07049 0.972818C3.50974 0.809814 3.97681 0.73495 4.445 0.752504H9.20667C9.93356 0.749936 10.6352 1.01889 11.1742 1.50667L13.6408 3.775C13.904 4.00174 14.1166 4.28134 14.2646 4.59563C14.4126 4.90991 14.4928 5.25184 14.5 5.59917Z" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <svg xmlns="http://www.w3.org/2000/svg" width="7" height="7" viewBox="0 0 7 7" fill="none" class="charity-form__document-icon-corner">
                      <path d="M0.75 0.75V3.74667C0.75 4.1525 0.9125 4.5425 1.2 4.83C1.48907 5.11781 1.88042 5.27932 2.28833 5.27917H5.72583" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                  }

                  <div class="charity-form__document-details">
                    <p
                      class="charity-form__document-name"
                      [customTooltip]="(document.fileName | filenameExtractor)"
                      [tooltipDisabled]="(document.fileName | filenameExtractor).length <= 80">
                      {{ document.fileName | filenameExtractor | truncateFilename }}
                    </p>
                    <p class="charity-form__document-size"
                       [class.charity-form__document-size--error]="document.status === 'error'">
                      {{ document.status === 'error' ? document.errorMessage : document.fileSizeFormatted }}
                    </p>
                  </div>

                  @if (document.status === 'success') {
                  <button
                    type="button"
                    class="charity-form__document-preview-button"
                    (click)="previewDocument(document)">
                    {{ 'settings.charity_details_form.documents.preview' | translate }}
                  </button>
                  }
                </div>

                <button
                  type="button"
                  class="charity-form__document-delete-button"
                  (click)="deleteDocument($index)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
                    <path d="M0.667969 3.33203H12.668" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M11.3372 3.33203V12.6654C11.3372 13.332 10.6706 13.9987 10.0039 13.9987H3.33724C2.67057 13.9987 2.00391 13.332 2.00391 12.6654V3.33203" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M4.00391 3.33268V1.99935C4.00391 1.33268 4.67057 0.666016 5.33724 0.666016H8.00391C8.67057 0.666016 9.33724 1.33268 9.33724 1.99935V3.33268" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M5.33203 6.66602V10.666" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M8.00391 6.66602V10.666" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
              }
            </div>
          </div>
          }

          <!-- Mobile Back/Next navigation (visible only on mobile) -->
          <div class="charity-form__mobile-nav">
            <button
              type="button"
              class="charity-form__mobile-nav-btn"
              [disabled]="isBackDisabled()"
              (click)="onBack()">
              {{ 'settings.charity_details_form.back' | translate }}
            </button>
            <button
              type="button"
              class="charity-form__mobile-nav-btn"
              (click)="onNext()">
              {{ 'settings.charity_details_form.next' | translate }}
            </button>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div class="charity-form__navigation">
          <button
            class="charity-form__nav-button charity-form__nav-button--back"
            type="button"
            [disabled]="isBackDisabled()"
            (click)="onBack()">
            {{ 'settings.charity_details_form.back' | translate }}
          </button>
          <button
            class="charity-form__nav-button charity-form__nav-button--next"
            type="button"
            (click)="onNext()">
            {{ 'settings.charity_details_form.next' | translate }}
          </button>
        </div>
      </div>
      }

      <!-- Gallery Step -->
      @if (!isLoadingDropdowns && currentStep === FormStep.GALLERY) {
      <div class="charity-form__form-container charity-form__form-container--documents charity-form__form-container--gallery-step">
        <!-- Gallery Content -->
        <div class="charity-form__documents-content">
          <!-- Mobile section header (title + description, visible only on mobile) -->
          <div class="charity-form__documents-mobile-header">
            <p class="charity-form__documents-title">
              {{ 'settings.charity_details_form.gallery_page_title' | translate }}
            </p>
            <p class="charity-form__documents-mobile-desc">
              {{ 'settings.charity_details_form.gallery.description' | translate }}@if (isFieldRequired('organisationImages')) {<span class="charity-form__required">*</span>}
            </p>
          </div>

          <!-- Description (desktop only) -->
          <p class="charity-form__documents-description">
            {{ 'settings.charity_details_form.gallery.description' | translate }}@if (isFieldRequired('organisationImages')) {<span class="charity-form__required">*</span>}
          </p>
          @if (galleryNextAttempted && isFieldRequired('organisationImages') && !hasValidGalleryData()) {
            <p class="charity-form__upload-inline-error">{{ 'settings.charity_details_form.gallery.error_required' | translate }}</p>
          }

          <!-- Upload Area -->
          <div class="charity-form__documents-upload-area"
               (dragover)="onGalleryMediaDragOver($event)"
               (drop)="onGalleryMediaDrop($event)"
               (click)="galleryMediaFileInput.click()">
            <div class="charity-form__documents-upload-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.3334 5.33333L8.00002 2L4.66669 5.33333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 2V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <p class="charity-form__documents-upload-text">
              {{ 'settings.charity_details_form.gallery.upload_files' | translate }}
            </p>
            <p class="charity-form__documents-upload-formats">
              {{ 'settings.charity_details_form.gallery.supported_formats' | translate }}
            </p>
            <input
              #galleryMediaFileInput
              type="file"
              class="charity-form__file-input"
              accept="image/jpeg,image/bmp,image/png,image/svg+xml,image/webp,video/mp4,video/quicktime,video/mpeg,video/ogg,video/x-msvideo,video/webm,video/3gpp,video/3gpp2,video/avi,.jpg,.jpeg,.bmp,.png,.svg,.webp,.mp4,.mov,.mpeg,.mpg,.ogg,.avi,.webm,.3gp,.3g2"
              (change)="onGalleryMediaFileSelected($event)"
              hidden>
          </div>

          <!-- Uploading Progress Indicator -->
          @if (isUploadingGalleryMedia) {
          <div class="charity-form__upload-progress">
            <div class="charity-form__upload-progress-info">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.6667 1.66666H5C4.55797 1.66666 4.13405 1.84225 3.82149 2.15481C3.50893 2.46737 3.33333 2.89129 3.33333 3.33332V16.6667C3.33333 17.1087 3.50893 17.5326 3.82149 17.8452C4.13405 18.1577 4.55797 18.3333 5 18.3333H15C15.442 18.3333 15.866 18.1577 16.1785 17.8452C16.4911 17.5326 16.6667 17.1087 16.6667 16.6667V6.66666L11.6667 1.66666Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.6667 1.66666V6.66666H16.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span class="charity-form__upload-progress-text">{{ 'settings.charity_details_form.gallery.uploading' | translate }}</span>
            </div>
            <div class="charity-form__upload-progress-bar">
              <div class="charity-form__upload-progress-bar-fill"></div>
            </div>
          </div>
          }

          <!-- Uploaded Media Section -->
          @if (uploadedGalleryMedia.length > 0) {
          <div class="charity-form__documents-list-section">
            <div class="charity-form__documents-list-header">
              <p class="charity-form__documents-list-title">
                <span class="charity-form__documents-list-title-text">
                  {{ 'settings.charity_details_form.gallery.uploaded_files' | translate }}
                </span>
              </p>
            </div>

            <div class="charity-form__documents-list">
              @for (media of uploadedGalleryMedia; track $index) {
              <div class="charity-form__document-item"
                   [class.charity-form__document-item--error]="media.status === 'error'">
                <div class="charity-form__document-info">
                  @if (media.status === 'error') {
                  <div class="charity-form__document-icon charity-form__document-icon--error">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="1.5"/>
                      <path d="M10 6V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      <circle cx="10" cy="13" r="0.5" fill="currentColor"/>
                    </svg>
                  </div>
                  } @else {
                  <!-- Gallery Media Icon (for both images and videos) -->
                  <div class="charity-form__document-icon">
                    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <!-- Main body -->
                      <path d="M0.75 6.08333C0.75 4.21667 0.75 3.28333 1.11333 2.57C1.43291 1.94282 1.94282 1.43291 2.57 1.11333C3.28333 0.75 4.21667 0.75 6.08333 0.75H10.4167C12.2833 0.75 13.2167 0.75 13.93 1.11333C14.5572 1.43291 15.0671 1.94282 15.3867 2.57C15.75 3.28333 15.75 4.21667 15.75 6.08333V10.4167C15.75 12.2833 15.75 13.2167 15.3867 13.93C15.0671 14.5572 14.5572 15.0671 13.93 15.3867C13.2167 15.75 12.2833 15.75 10.4167 15.75H6.08333C4.21667 15.75 3.28333 15.75 2.57 15.3867C1.94282 15.0671 1.43291 14.5572 1.11333 13.93C0.75 13.2167 0.75 12.2833 0.75 10.4167V6.08333Z" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <!-- Circle and curve at lower right -->
                      <g transform="translate(3, 2.5)">
                        <path d="M12.7725 4.075C7.26417 3.31833 2.51417 7.40833 2.83333 12.8333M0.75 2.41667C0.75 2.85869 0.925595 3.28262 1.23816 3.59518C1.55072 3.90774 1.97464 4.08333 2.41667 4.08333C2.85869 4.08333 3.28262 3.90774 3.59518 3.59518C3.90774 3.28262 4.08333 2.85869 4.08333 2.41667C4.08333 1.97464 3.90774 1.55072 3.59518 1.23816C3.28262 0.925595 2.85869 0.75 2.41667 0.75C1.97464 0.75 1.55072 0.925595 1.23816 1.23816C0.925595 1.55072 0.75 1.97464 0.75 2.41667Z" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </g>
                      <!-- Curve at lower left -->
                      <g transform="translate(0, 8)">
                        <path d="M0.75 0.804733C3.06667 0.4839 5.14583 1.60307 6.27 3.38807" stroke="#96080B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </g>
                    </svg>
                  </div>
                  }

                  <div class="charity-form__document-details">
                    <p
                      class="charity-form__document-name"
                      [customTooltip]="(media.fileName | filenameExtractor)"
                      [tooltipDisabled]="(media.fileName | filenameExtractor).length <= 80">
                      {{ media.fileName | filenameExtractor | truncateFilename }}
                    </p>
                    <p class="charity-form__document-size"
                       [class.charity-form__document-size--error]="media.status === 'error'">
                      {{ media.status === 'error' ? media.errorMessage : media.fileSizeFormatted }}
                    </p>
                  </div>

                  @if (media.status === 'success') {
                  <button
                    type="button"
                    class="charity-form__document-preview-button"
                    (click)="previewGalleryMedia(media)">
                    {{ 'settings.charity_details_form.gallery.preview' | translate }}
                  </button>
                  }
                </div>

                <button
                  type="button"
                  class="charity-form__document-delete-button"
                  (click)="deleteGalleryMedia($index)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
                    <path d="M0.667969 3.33203H12.668" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M11.3372 3.33203V12.6654C11.3372 13.332 10.6706 13.9987 10.0039 13.9987H3.33724C2.67057 13.9987 2.00391 13.332 2.00391 12.6654V3.33203" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M4.00391 3.33268V1.99935C4.00391 1.33268 4.67057 0.666016 5.33724 0.666016H8.00391C8.67057 0.666016 9.33724 1.33268 9.33724 1.99935V3.33268" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M5.33203 6.66602V10.666" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M8.00391 6.66602V10.666" stroke="#96080B" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
              }
            </div>
          </div>
          }

          <!-- Mobile Back/Next navigation (visible only on mobile) -->
          <div class="charity-form__mobile-nav">
            <button
              type="button"
              class="charity-form__mobile-nav-btn"
              [disabled]="isBackDisabled()"
              (click)="onBack()">
              {{ 'settings.charity_details_form.back' | translate }}
            </button>
            <button
              type="button"
              class="charity-form__mobile-nav-btn"
              disabled>
              {{ 'settings.charity_details_form.next' | translate }}
            </button>
          </div>
        </div>

        <!-- Navigation Buttons -->
        <div class="charity-form__navigation">
          <button
            class="charity-form__nav-button charity-form__nav-button--back"
            type="button"
            [disabled]="isBackDisabled()"
            (click)="onBack()">
            {{ 'settings.charity_details_form.back' | translate }}
          </button>
          <button
            class="charity-form__nav-button charity-form__nav-button--next"
            type="button"
            disabled>
            {{ 'settings.charity_details_form.next' | translate }}
          </button>
        </div>
      </div>
      }
    </div>
  </div>

  <!-- Mobile: Fixed Bottom Buttons -->
  <div class="charity-form__mobile-footer">
    <button
      class="charity-form__button charity-form__button--preview"
      type="button"
      [disabled]="!isPreviewAvailable"
      (click)="onPreview()">
      {{ 'settings.charity_details_form.preview' | translate }}
    </button>
    @if (isDraftStatus) {
      <button
        class="charity-form__button charity-form__button--draft"
        type="button"
        (click)="onSaveAsDraft()">
        {{ 'settings.charity_details_form.save_as_draft' | translate }}
      </button>
    }
    <button
      class="charity-form__button charity-form__button--submit"
      type="button"
      [disabled]="isSubmitDisabled"
      (click)="onSubmit()">
      {{ 'settings.charity_details_form.submit' | translate }}
    </button>
  </div>

</div>
